import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { camerasAPI, zonesAPI, detectProfilesAPI } from '../utils/api';
import { canEnableCapability, getAnalyticCapability, getPpeItemCapability } from '../utils/detectionCapabilities';
import { useSiteStore } from '../stores/siteStore';
import { useLiveStreamStore } from '../stores/liveStreamStore';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import ZoneAssignmentModal from '../components/zones/ZoneAssignmentModal';
import { SettingsSearchBar, SettingsCard, SettingsGroup, SliderRow, ToggleRow, countModified } from '../components/cameras/AdvancedSettingControls';
import {
  ArrowLeftIcon,
  VideoCameraIcon,
  Cog6ToothIcon,
  MapPinIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  UserIcon,
  EyeIcon,
  TrashIcon,
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  CameraIcon,
  ChevronDownIcon,
  AdjustmentsHorizontalIcon,
  TruckIcon,
  ClockIcon,
  BoltIcon,
  WrenchScrewdriverIcon,
  BeakerIcon,
  DocumentCheckIcon,
  FireIcon,
  NoSymbolIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

// Default confidence thresholds
const DEFAULT_CONFIDENCE_THRESHOLDS = {
  person: 0.5,
  fire: 0.5,
  smoke: 0.5,
  vehicle: 0.7,
};

// PPE items — keys match seed-detection-profiles.js and AI engine worn_class_mapping
const PPE_ITEMS = [
  { key: 'helmet',        label: 'Hard Hat',       description: 'Head protection' },
  { key: 'vest',          label: 'Safety Vest',     description: 'High-visibility vest' },
  { key: 'gloves',        label: 'Gloves',          description: 'Hand protection' },
  { key: 'boots',         label: 'Safety Boots',    description: 'Foot protection' },
  { key: 'glasses',       label: 'Safety Glasses',  description: 'Eye protection' },
  { key: 'mask',          label: 'Face Mask',       description: 'Respiratory protection' },
  { key: 'harness',       label: 'Safety Harness',  description: 'Fall arrest system' },
  { key: 'earProtection', label: 'Ear Protection',  description: 'Hearing protection' },
];

// Default per-item PPE thresholds — mirrors seed-detection-profiles.js construction profile
const DEFAULT_PPE_WORN = {
  helmet: 0.50, vest: 0.45, gloves: 0.50, boots: 0.48,
  glasses: 0.55, mask: 0.50, harness: 0.50, earProtection: 0.52,
};
const DEFAULT_PPE_VIOLATION = {
  helmet: 0.65, vest: 0.65, gloves: 0.68, boots: 0.65,
  glasses: 0.72, mask: 0.70, harness: 0.70, earProtection: 0.72,
};

// Default vehicle proximity settings
const DEFAULT_VEHICLE_PROXIMITY = {
  enabled: false,
  confidence: 0.7,
  minSizeRatio: 0.02,  // 2% of frame
  cooldown: 30,  // seconds
  // Distance thresholds in meters
  criticalDistance: 2.0,  // < 2m - immediate danger
  highDistance: 5.0,      // 2-5m - dangerous proximity
  mediumDistance: 10.0,   // 5-10m - warning zone
  // By default only alert on vehicles actively in transition (moving). When true,
  // idle/stationary vehicles near a person also trigger proximity alerts.
  detectIdleVehicles: false,
  // Advanced moving/idle classifier tuning. Frames the motion is measured over, the
  // net-displacement threshold (fraction of the vehicle's bbox diagonal), the
  // directional-consistency threshold (net/gross path — rejects jitter), and how many
  // consecutive "moving" frames are required before a moving-only alert fires.
  motionWindowFrames: 15,
  idleDisplacementRatio: 0.06,
  motionDirectionRatio: 0.5,
  motionConfirmFrames: 3,
};

// Default processing settings
const DEFAULT_PROCESSING_SETTINGS = {
  targetFps: 5,           // Target frames per second for processing
  priority: 'normal',     // Camera priority: critical, high, normal, low
};

// Default alert cooldowns (seconds) per detection type
const DEFAULT_ALERT_COOLDOWNS = {
  ppe: 30,
  fire: 120,
  fall: 60,
  vehicle_proximity: 30,
  crowd: 60,
  trespass: 30,
};

// Default crowd density settings
const DEFAULT_CROWD_DENSITY = {
  maxPersons: 15,
  cooldown: 60,
};

// Default advanced detection thresholds (mirrors ai-engine config.py defaults)
const DEFAULT_ADVANCED_THRESHOLDS = {
  fire_high_confidence_threshold: 0.95,   // Skip consecutive-frame check above this confidence
  fire_min_area_ratio: 0.004,             // Min fire bbox as fraction of frame area (0.4%)
  fire_max_area_ratio: 0.30,              // Max fire bbox as fraction of frame area (30%) — reject walls/vehicles
  fire_max_aspect_ratio: 2.5,             // Max fire bbox width:height ratio
  fire_ppe_secondary_confidence: 0.3,    // Min PPE confidence to suppress fire as false positive
  fire_person_overlap_threshold: 0.85,    // % of fire bbox overlapping a person to suppress as vest
  fire_proximity_distance_px: 200,        // Max person-to-fire centre distance (px) to raise a proximity alert — FOV-dependent
  fire_color_max_area_ratio: 0.70,        // Max blob size (fraction of frame) for the colour-based fire path
  fire_min_bg_deviation: 20,              // Min mean deviation from background model for a colour-fire blob (rejects static objects)
  fire_min_motion_ratio: 0.15,            // Min flicker/motion ratio for a colour-fire blob (rejects static bollards/barriers)
  fire_min_texture_std: 15,               // Min texture std-dev for a colour-fire blob (rejects uniform lights/objects)
  pose_confidence: 0.25,                  // Pose model confidence for fall detection
  fall_min_keypoint_confidence: 0.3,      // Min confidence for a keypoint to count in fall logic
  ppe_min_person_height: 0,               // Min person height (px) to run PPE check — 0 = disabled
  // Smoke false-positive controls
  smoke_max_mean_brightness: 192,         // Reject near-white haze/concrete brighter than this (0-255 V)
  smoke_min_texture_std: 8,               // Reject uniform surfaces (flat concrete/sky) below this std-dev
  smoke_min_motion_ratio: 0.03,           // Min frame-to-frame drift for real smoke (lower = more sensitive)
  smoke_temporal_verify: 1,               // 1 = require smoke to drift across frames, 0 = disabled
  smoke_min_bg_deviation: 15,             // Min mean deviation from background model to count as smoke (not static haze)
  smoke_max_static_frames: 8,             // Max consecutive frames smoke can be static before being rejected
  smoke_required_consecutive: 4,          // Consecutive frames smoke must appear before alert fires
};

// PPE detection behaviour settings (saved as detectionConfig.ppeDetection)
// Field names MUST match what detection_engine._get_camera_ppe_setting() uses
const DEFAULT_PPE_DETECTION_SETTINGS = {
  hardhatColorCheckEnabled: true,   // Suppress no-helmet via colour analysis
  hardhatColorMinRatio: 0.40,       // Fraction of head region with hat colours to confirm hat
  hardhatGraceFrames: 20,           // Frames to keep suppressing after hat last seen by colour
  vestColorCheckEnabled: true,      // Suppress no-vest via colour/stripe analysis
  vestColorMinRatio: 0.05,          // Min neon/stripe ratio to confirm vest by colour
  vestColorGraceFrames: 30,         // Frames to keep suppressing after vest last seen by colour
  hardhatClassifierEnabled: false,  // Per-camera secondary hardhat classifier (ML model gate)
  vestClassifierEnabled: false,     // Per-camera secondary vest classifier (ML model gate)
  vestClassifierShadow: true,       // Per-camera shadow mode: run + log classifier without acting (only applies when enable is OFF)
  vestClassifierConf: 0.75,         // Min classifier confidence to suppress no_vest (lower = more lenient, fewer FPs)
  vestClassifierGraceFrames: 15,    // Frames to keep suppressing no_vest after classifier last confirmed a vest (bridges one-frame glitches)
  // Vest/Helmet detector (PRIMARY for vest + helmet). Unlike the classifier gates
  // above — which can only suppress an existing violation — this model OWNS both
  // verdicts and can also raise them, so turning it off hands vest/helmet back to
  // the general PPE model. Defaults mirror PPE_VH_* in the AI engine's .env.
  vhEnabled: true,                  // Vest/Helmet detector decides vest + helmet
  vhShadow: false,                  // true = log the verdict only, don't apply it
  vhConf: 0.5,                      // Min detection confidence for a VH verdict to count
  vhContainment: 0.5,               // Fraction of an item box that must sit inside the person box
  vestAbstainGateEnabled: false,    // Per-camera torso-assessability abstain gate (defer no_vest when torso unassessable)
  firePersonOverlapCheck: true,      // Per-camera enable/disable fire-person overlap suppression
  fireColorDetectionEnabled: false,
  fireColorCheckEnabled: true,
  fireColorMinRatio: 0.15,
  fireHueMinStd: 11,
  fireVMinStd: 22,                  // Min brightness (V) std-dev in fire-coloured pixels — real flames have bright core + dark edges
  fireYellowMinRatio: 0.035,        // Min yellow-orange pixel ratio for the fire-colour check (flame tips are always yellow)
  fireRedCoreMinRatio: 0.02,        // Min deep-red pixel ratio for the fire-colour check (flame base is deep red)
  fireColorMinRatioSmall: 0.05,     // Min fire-colour ratio for the initial (small-bbox) model-path colour check
  fireRequiredConsecutive: 3,
  smokeColorMinRatio: 0.10,
  smokeLargeBypassThreshold: 0.30,
  smokeSkyExclusionRatio: 0.30,
  smokeAreaRatioMin: 0.02,
  smokeAreaRatioMax: 0.80,
  glassesFaceRegionFraction: 0.25,
  glassesFaceUpscale: 3,
  glassesFaceCropConf: 0.20,
  faceVisSkinMinRatio: 0.10,        // Min skin-tone ratio in the face region for the face to count as visible (gates glasses checks)
  helmetWornMaxThreshold: 0.55,     // Cap on worn-helmet confidence threshold
  historyFrames: 3,                 // Frames window for PPE presence voting
  violationFrameOverrides: {},      // Per-class frame override e.g. {"no_helmet": 5}
  enabledViolationClasses: [],      // Whitelist of violation classes to report (empty = all)
  complianceGraceRatio: 0.7,        // Min fraction of recent frames a worker must be compliant to suppress a violation (lower = fewer false fires when a worker briefly turns)
  complianceMemoryFrames: 20,       // Rolling window size (frames) for compliance-decay check — larger = slower decay, harder to trigger false fires on brief look-aways
  vestRecoveryGraceFrames: 45,      // Frames to keep suppressing no-vest after the multi-frame recovery last re-found a vest (side-on cameras need a longer window)
  ppeBurstWindow: 8,                // Vest-presence evidence buffer size for burst confirmation
  ppeBurstMinSamples: 5,            // Min evidence frames before a no-vest can be confirmed
  ppeBurstMaxPresentRatio: 0.25,    // If a vest was present in more than this fraction of the window, hold the no-vest
  bootsColorMinRatio: 0.15,         // Min dark-footwear pixel ratio to suppress a no-boots violation
  occlusionCautionThreshold: 0.2,   // Occlusion ratio above which colour masking of the occluder kicks in
  occlusionSuppressThreshold: 0.4,  // Occlusion ratio above which synthetic PPE violations are suppressed
  vestTier3Strict: false,           // Require a structured cross-stripe (not diffuse silver) for the stripe-only vest tier — stops grey clothing reading as a vest
  vestTier3SilverMin: 0.12,         // Silver-ratio floor for the strict vest stripe tier
  vestTier1MeanSatMin: 60,          // Min mean torso saturation for the Tier-1 colour+stripe vest path (rejects dark/grey clothing)
  vestTier3LooseSilverMin: 0.08,    // Silver-ratio floor for the loose (non-strict) stripe-only vest tier
  hardhatSecondaryConf: 0.25,       // Confidence for the secondary PPE-model helmet re-check that suppresses no-helmet
  harnessConf: 0.6,                 // Min detection confidence for the dedicated harness / no-harness model
  harnessCooldown: 45,              // Seconds between repeat harness alerts for the same person in the same zone
  harnessShadowMode: true,          // Ships ON: log "would-alert" only. Turn OFF per camera to go live after validating
  harnessBurstWindow: 8,            // Harness-presence evidence buffer size for burst confirmation
  harnessBurstMinSamples: 2,        // Min evidence frames before a no-harness can be confirmed
  harnessBurstMaxPresentRatio: 0.25,// If a harness was present in more than this fraction of the window, hold the no-harness
  harnessComplianceMemoryEnabled: true, // Apply compliance-memory grace to harness violations
  harnessGraceFrames: 45,           // Frames to keep suppressing no_harness after the detector last confirmed a harness
  assumedPersonHeightMeters: 1.7,   // Assumed real-world person height used to convert pixels to metres for vehicle proximity
  hardhatWhiteLenient: false,       // Enable the lenient specular path that rescues WHITE hard hats the geometry check misses
  hardhatWhiteMinRatio: 0.20,       // Min white/grey ratio in the head region for the lenient white-hat path
};

// Helper function to extract PPE confidence from either old or new format
function extractPPEConfidence(ppeValue, defaultValue = 0.6) {
  // If it's already a number (old format), return it
  if (typeof ppeValue === 'number') {
    return ppeValue;
  }

  // If it's an object (new format with worn/violation), extract a single value
  if (ppeValue && typeof ppeValue === 'object') {
    const worn = ppeValue.worn;
    if (worn && typeof worn === 'object') {
      // Use vest threshold if available (most sensitive)
      if (worn.vest && typeof worn.vest === 'number') {
        return worn.vest;
      }
      // Otherwise, average all worn thresholds
      const wornValues = Object.values(worn).filter(v => typeof v === 'number');
      if (wornValues.length > 0) {
        return wornValues.reduce((a, b) => a + b, 0) / wornValues.length;
      }
    }
  }

  // Fallback to default
  return defaultValue;
}

// Priority options
const PRIORITY_OPTIONS = [
  { id: 'critical', name: 'Critical', description: 'Processed first, highest frame rate', color: 'red' },
  { id: 'high', name: 'High', description: 'High priority processing', color: 'orange' },
  { id: 'normal', name: 'Normal', description: 'Standard processing priority', color: 'blue' },
  { id: 'low', name: 'Low', description: 'Background processing, saves resources', color: 'gray' },
];

// Available analytics types - matches AI engine PPE model capabilities
const ANALYTICS_OPTIONS = [
  // ===== CRITICAL SAFETY =====
  {
    id: 'fire_detection',
    name: 'Fire Detection',
    description: 'Detect fire and flames in the scene',
    icon: ExclamationTriangleIcon,
    severity: 'CRITICAL',
    category: 'fire',
  },
  {
    id: 'smoke_detection',
    name: 'Smoke Detection',
    description: 'Detect smoke that may indicate fire hazard',
    icon: ExclamationTriangleIcon,
    severity: 'HIGH',
    category: 'fire',
  },
  {
    id: 'fall_detection',
    name: 'Fall Detection',
    description: 'Detect when a person falls down',
    icon: ExclamationTriangleIcon,
    severity: 'CRITICAL',
    category: 'behavior',
  },
  {
    id: 'trespassing',
    name: 'Trespassing Detection',
    description: 'Alert when unauthorized person enters restricted area',
    icon: ExclamationTriangleIcon,
    severity: 'CRITICAL',
    requiresZones: true,
    category: 'zone',
  },
  // ===== PPE DETECTION =====
  {
    id: 'ppe_hardhat',
    name: 'Hard Hat Detection',
    description: 'Detect missing hard hats (head_nohelmet)',
    icon: ShieldCheckIcon,
    severity: 'HIGH',
    category: 'ppe',
  },
  {
    id: 'ppe_vest',
    name: 'Safety Vest Detection',
    description: 'Detect missing safety vests',
    icon: ShieldCheckIcon,
    severity: 'MEDIUM',
    category: 'ppe',
  },
  {
    id: 'ppe_glasses',
    name: 'Safety Glasses Detection',
    description: 'Detect missing safety glasses (no_glasses)',
    icon: EyeIcon,
    severity: 'MEDIUM',
    category: 'ppe',
  },
  {
    id: 'ppe_gloves',
    name: 'Gloves Detection',
    description: 'Detect missing gloves (no_gloves)',
    icon: ShieldCheckIcon,
    severity: 'LOW',
    category: 'ppe',
  },
  {
    id: 'ppe_mask',
    name: 'Face Mask Detection',
    description: 'Detect missing face masks (no_mask)',
    icon: ShieldCheckIcon,
    severity: 'MEDIUM',
    category: 'ppe',
  },
  {
    id: 'ppe_boots',
    name: 'Safety Boots Detection',
    description: 'Detect improper footwear (barefoot/sandals)',
    icon: ShieldCheckIcon,
    severity: 'MEDIUM',
    category: 'ppe',
  },
  {
    id: 'ppe_ear_protection',
    name: 'Ear Protection Detection',
    description: 'Detect missing ear protection',
    icon: ShieldCheckIcon,
    severity: 'LOW',
    category: 'ppe',
  },
  {
    id: 'ppe_harness',
    name: 'Safety Harness Detection',
    description: 'Detect safety harness presence',
    icon: ShieldCheckIcon,
    severity: 'HIGH',
    category: 'ppe',
  },
  {
    id: 'zone_breach',
    name: 'Zone Breach Detection',
    description: 'Alert when someone enters a restricted zone',
    icon: MapPinIcon,
    severity: 'CRITICAL',
    requiresZones: true,
    category: 'zone',
  },
  {
    id: 'crowd_detection',
    name: 'Crowd Detection',
    description: 'Detect overcrowding in an area',
    icon: UserIcon,
    severity: 'HIGH',
    category: 'behavior',
  },
  {
    id: 'vehicle_proximity',
    name: 'Vehicle Proximity Detection',
    description: 'Alert when person is dangerously close to vehicles',
    icon: TruckIcon,
    severity: 'CRITICAL',
    category: 'vehicle',
    hasAdvancedSettings: true,
  },
  // ===== EQUIPMENT & VEHICLE INTELLIGENCE =====
  {
    id: 'equipment_exclusion',
    name: 'Equipment Exclusion Zone',
    description: 'Alert when person enters heavy equipment exclusion zone',
    icon: NoSymbolIcon,
    severity: 'CRITICAL',
    category: 'equipment',
  },
  {
    id: 'vehicle_speed',
    name: 'Vehicle Speed Estimation',
    description: 'Detect vehicles exceeding speed limits',
    icon: TruckIcon,
    severity: 'HIGH',
    category: 'vehicle',
  },
  {
    id: 'license_plate',
    name: 'License Plate Recognition',
    description: 'Read and verify vehicle license plates',
    icon: TruckIcon,
    severity: 'HIGH',
    category: 'vehicle',
  },
  {
    id: 'idle_detection',
    name: 'Equipment Idle Detection',
    description: 'Detect equipment idle beyond time threshold',
    icon: ClockIcon,
    severity: 'MEDIUM',
    category: 'equipment',
  },
  // ===== ENVIRONMENTAL & HOUSEKEEPING =====
  {
    id: 'debris_detection',
    name: 'Housekeeping / Debris Detection',
    description: 'Detect scattered tools, materials, and clutter',
    icon: WrenchScrewdriverIcon,
    severity: 'MEDIUM',
    category: 'housekeeping',
  },
  {
    id: 'spill_detection',
    name: 'Spill Detection',
    description: 'Detect liquid and chemical spills',
    icon: BeakerIcon,
    severity: 'HIGH',
    category: 'environmental',
  },
  {
    id: 'barricade_detection',
    name: 'Barricade / Signage Compliance',
    description: 'Verify barricades, cones, and warning signs are in place',
    icon: ShieldCheckIcon,
    severity: 'HIGH',
    category: 'compliance',
  },
  {
    id: 'exit_obstruction',
    name: 'Blocked Emergency Exit',
    description: 'Detect when emergency exits are obstructed',
    icon: ExclamationTriangleIcon,
    severity: 'CRITICAL',
    requiresZones: true,
    category: 'compliance',
  },
  // ===== PROCESS & PERMIT COMPLIANCE =====
  {
    id: 'hotwork_detection',
    name: 'Hot Work Fire Watch',
    description: 'Ensure fire watch personnel present during hot work',
    icon: FireIcon,
    severity: 'CRITICAL',
    category: 'permit',
  },
  {
    id: 'confined_space',
    name: 'Confined Space Attendant',
    description: 'Monitor confined space entry and attendant presence',
    icon: DocumentCheckIcon,
    severity: 'CRITICAL',
    requiresZones: true,
    category: 'permit',
  },
  {
    id: 'scaffolding_detection',
    name: 'Scaffolding Safety',
    description: 'Detect scaffolding components: guardrails, toe boards',
    icon: WrenchScrewdriverIcon,
    severity: 'HIGH',
    category: 'compliance',
  },
  // ===== BEHAVIORAL SAFETY =====
  {
    id: 'lifting_detection',
    name: 'Improper Lifting Detection',
    description: 'Detect unsafe lifting posture using pose analysis',
    icon: ExclamationTriangleIcon,
    severity: 'HIGH',
    category: 'behavior',
  },
  {
    id: 'phone_usage',
    name: 'Phone Usage Detection',
    description: 'Detect phone usage in hazard zones (pose-based)',
    icon: UserIcon,
    severity: 'MEDIUM',
    category: 'behavior',
  },
  {
    id: 'smoking_detection',
    name: 'Smoking in Prohibited Areas',
    description: 'Detect smoking violations in restricted zones',
    icon: FireIcon,
    severity: 'HIGH',
    category: 'behavior',
  },
  {
    id: 'fatigue_detection',
    name: 'Worker Fatigue Detection',
    description: 'Detect drowsiness and low activity patterns',
    icon: ClockIcon,
    severity: 'MEDIUM',
    category: 'behavior',
  },
  {
    id: 'height_posture',
    name: 'Unsafe Posture at Height',
    description: 'Detect leaning/unsafe posture in height work zones',
    icon: ExclamationTriangleIcon,
    severity: 'HIGH',
    requiresZones: true,
    category: 'behavior',
  },
  {
    id: 'harness_enforcement',
    name: 'Context-Aware Harness Check',
    description: 'Enforce harness wear in height/scaffolding zones',
    icon: ShieldCheckIcon,
    severity: 'CRITICAL',
    requiresZones: true,
    category: 'ppe',
  },
];

// Zone types
const ZONE_TYPES = [
  { id: 'restricted', name: 'Restricted Zone', color: '#EF4444' },
  { id: 'hazard', name: 'Hazard Zone', color: '#F97316' },
  { id: 'general', name: 'General Zone', color: '#3B82F6' },
  { id: 'entry', name: 'Entry Zone', color: '#10B981' },
  { id: 'exit', name: 'Exit Zone', color: '#8B5CF6' },
  { id: 'confined_space', name: 'Confined Space', color: '#EC4899' },
  { id: 'crane_zone', name: 'Crane Operating Zone', color: '#F59E0B' },
  { id: 'excavation', name: 'Excavation Zone', color: '#92400E' },
  { id: 'chemical', name: 'Chemical Storage Zone', color: '#7C3AED' },
  { id: 'electrical', name: 'Electrical Work Zone', color: '#FBBF24' },
  { id: 'hot_work', name: 'Hot Work Zone', color: '#DC2626' },
  { id: 'height_work', name: 'Height Work / Scaffolding', color: '#059669' },
];

// Zone selector dropdown component
function ZoneSelector({ zones, selectedZones, onChange, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isAllSelected = selectedZones.includes('all') || selectedZones.length === 0;

  const handleToggleAll = () => {
    if (isAllSelected) {
      // If currently all, and we have zones, select first zone
      if (zones.length > 0) {
        onChange([zones[0].id]);
      }
    } else {
      onChange(['all']);
    }
  };

  const handleToggleZone = (zoneId) => {
    if (selectedZones.includes('all')) {
      // Switching from "all" to specific zone
      onChange([zoneId]);
    } else if (selectedZones.includes(zoneId)) {
      // Remove zone
      const newSelection = selectedZones.filter(id => id !== zoneId);
      // If no zones selected, default to all
      onChange(newSelection.length > 0 ? newSelection : ['all']);
    } else {
      // Add zone
      onChange([...selectedZones, zoneId]);
    }
  };

  const getDisplayText = () => {
    if (isAllSelected) return 'Entire Frame';
    if (selectedZones.length === 1) {
      const zone = zones.find(z => z.id === selectedZones[0]);
      return zone?.name || 'Selected Zone';
    }
    return `${selectedZones.length} zones`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          'flex items-center gap-2 px-3 py-1.5 text-sm border rounded-lg',
          disabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white hover:bg-gray-50 text-gray-700'
        )}
      >
        <MapPinIcon className="w-4 h-4" />
        <span>{getDisplayText()}</span>
        <ChevronDownIcon className="w-3 h-3" />
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-56 bg-white border rounded-lg shadow-lg py-1">
          {/* Entire Frame option */}
          <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={handleToggleAll}
              className="rounded text-blue-600"
            />
            <span className="text-sm font-medium">Entire Frame</span>
          </label>

          {zones.length > 0 && (
            <>
              <div className="border-t my-1" />
              <div className="px-3 py-1 text-xs text-gray-500 font-medium">
                Specific Zones
              </div>
              <div className="max-h-48 overflow-y-auto">
              {zones.map(zone => {
                const zoneType = ZONE_TYPES.find(t => t.id === zone.type);
                return (
                  <label
                    key={zone.id}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={!isAllSelected && selectedZones.includes(zone.id)}
                      onChange={() => handleToggleZone(zone.id)}
                      className="rounded text-blue-600"
                    />
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: zoneType?.color || '#3B82F6' }}
                    />
                    <span className="text-sm">{zone.name}</span>
                  </label>
                );
              })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CapabilityBadge({ capability }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
        capability.badgeClass
      )}
      title={capability.description}
    >
      {capability.label}
    </span>
  );
}

export default function CameraConfigPage({ cameraIdOverride, embedded = false, initialCamera, deletedContext = false, snapshotFallback = null } = {}) {
  const params = useParams();
  const id = cameraIdOverride || params.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedSite } = useSiteStore();

  // State - new structure with analytics containing zone assignments
  const [analytics, setAnalytics] = useState(
    ANALYTICS_OPTIONS.map(opt => ({
      id: opt.id,
      enabled: false,
      zones: ['all'], // Default to entire frame
    }))
  );
  const [zones, setZones] = useState([]);
  const [confidenceThresholds, setConfidenceThresholds] = useState(DEFAULT_CONFIDENCE_THRESHOLDS);
  const [ppeWornThresholds, setPpeWornThresholds] = useState(DEFAULT_PPE_WORN);
  const [ppeViolationThresholds, setPpeViolationThresholds] = useState(DEFAULT_PPE_VIOLATION);
  const [showPpeDetail, setShowPpeDetail] = useState(false);
  const [vehicleProximitySettings, setVehicleProximitySettings] = useState(DEFAULT_VEHICLE_PROXIMITY);
  const [processingSettings, setProcessingSettings] = useState(DEFAULT_PROCESSING_SETTINGS);
  const [alertCooldowns, setAlertCooldowns] = useState(DEFAULT_ALERT_COOLDOWNS);
  const [crowdDensitySettings, setCrowdDensitySettings] = useState(DEFAULT_CROWD_DENSITY);
  const [advancedThresholds, setAdvancedThresholds] = useState(DEFAULT_ADVANCED_THRESHOLDS);
  const [ppeDetectionSettings, setPpeDetectionSettings] = useState(DEFAULT_PPE_DETECTION_SETTINGS);
  const [showAdvancedThresholds, setShowAdvancedThresholds] = useState(false);
  const [showPpeDetectionSettings, setShowPpeDetectionSettings] = useState(false);
  const [showAlertCooldowns, setShowAlertCooldowns] = useState(false);
  const [showCrowdDensitySettings, setShowCrowdDensitySettings] = useState(false);
  const [advancedSearch, setAdvancedSearch] = useState('');
  const advancedSearchActive = advancedSearch.trim().length > 0;
  const matchesAdvancedSearch = (keywords) =>
    !advancedSearchActive || keywords.toLowerCase().includes(advancedSearch.trim().toLowerCase());
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState([]);
  const [selectedZoneType, setSelectedZoneType] = useState('restricted');
  const [newZoneName, setNewZoneName] = useState('');
  const [selectedZone, setSelectedZone] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showZoneAssignmentModal, setShowZoneAssignmentModal] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [activeTab, setActiveTab] = useState('zones');
  // Vertex drag — { zoneId, vertexIndex } while dragging, null when idle
  const [draggingVertex, setDraggingVertex] = useState(null);
  // Whole-zone drag — { zoneId, lastX, lastY } in % coords
  const [draggingZone, setDraggingZone] = useState(null);

  const CONFIG_TABS = [
    { id: 'zones',      label: 'Zones',      icon: MapPinIcon },
    { id: 'detection',  label: 'Detection',  icon: AdjustmentsHorizontalIcon },
    { id: 'analytics',  label: 'Analytics',  icon: Cog6ToothIcon },
    { id: 'processing', label: 'Processing', icon: BoltIcon },
    { id: 'advanced',   label: 'Advanced',   icon: WrenchScrewdriverIcon },
    { id: 'info',       label: 'Info',       icon: InformationCircleIcon },
  ];

  // Memoize the onDrawingModeChange callback to prevent infinite loops
  const handleDrawingModeChange = useCallback((enabled) => {
    setIsDrawing(enabled);
    if (!enabled) {
      setCurrentPoints([]);
      setNewZoneName('');
    }
  }, []);

  // Reference image state
  const [referenceImage, setReferenceImage] = useState(null);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(false);
  const [snapshotError, setSnapshotError] = useState(null);

  // Live stream store for snapshot requests
  const { requestSnapshot, activeStreams, connected, connect } = useLiveStreamStore();
  const streamState = activeStreams[id];

  // Canvas ref
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const hasRequestedSnapshot = useRef(false);

  // Fetch camera data. Cache for 60s — camera detail (incl. detectionConfig)
  // is expensive to fetch and rarely changes outside this page.
  // When embedded inside CamerasPage we already have the full camera object
  // from the list query, so seed initialData to render instantly while the
  // detail query refreshes in the background.
  const { data, isLoading } = useQuery({
    queryKey: ['camera', id],
    queryFn: () => camerasAPI.getCamera(id),
    staleTime: 60_000,
    enabled: !!id,
    initialData: initialCamera && initialCamera.id === id
      ? { data: { data: initialCamera } }
      : undefined,
  });

  const camera = data?.data?.data;

  // Detection profiles change very rarely (admin-only edit). Cache aggressively.
  const { data: profilesData } = useQuery({
    queryKey: ['detection-profiles'],
    queryFn: async () => {
      const response = await detectProfilesAPI.getProfiles();
      return response.data?.profiles || [];
    },
    staleTime: 10 * 60_000, // 10 min
  });

  const profiles = profilesData || [];

  // Fetch available zones for the camera's site. Start with selectedSite so the
  // request can fire in parallel with the camera fetch; once camera resolves we
  // re-key on camera.siteId so the data is authoritative even on deep links.
  const zoneSiteId = camera?.siteId || selectedSite?.id;
  const { data: zonesData } = useQuery({
    queryKey: ['zones', zoneSiteId],
    queryFn: async () => {
      const response = await zonesAPI.getZones({ siteId: zoneSiteId });
      return response.data || [];
    },
    enabled: !!zoneSiteId,
    staleTime: 60_000,
  });

  // Parse detectionConfig once when the camera changes; never in JSX render
  // path. Previously this ran on every render via an inline JSON.parse inside
  // an IIFE in the Detection tab, which was a noticeable cost for cameras with
  // large analytics arrays.
  const parsedDetectionConfig = useMemo(() => {
    if (!camera?.detectionConfig) return null;
    return typeof camera.detectionConfig === 'string'
      ? JSON.parse(camera.detectionConfig)
      : camera.detectionConfig;
  }, [camera?.detectionConfig]);

  const cameraHasSavedPpe = !!(parsedDetectionConfig?.confidence_thresholds?.ppe?.worn);

  const availableZones = Array.isArray(zonesData) ? zonesData : [];

  // Load saved config when camera data loads
  useEffect(() => {
    // Initialize with profile ID if camera has one assigned
    if (camera && camera.detectionProfileId) {
      setSelectedProfileId(camera.detectionProfileId);
    } else {
      setSelectedProfileId(null);
    }

    // Reuse the memoized parse rather than re-parsing here.
    const rawConfig = parsedDetectionConfig;
    const hasSavedPpe = cameraHasSavedPpe;

    if (hasSavedPpe) {
      // Camera has its own saved per-PPE thresholds — ignore the profile entirely.
      // Profile dropdown is only available as an explicit "reset" action.
      const ct = rawConfig.confidence_thresholds;
      setConfidenceThresholds({
        person: ct.person ?? DEFAULT_CONFIDENCE_THRESHOLDS.person,
        fire: ct.fire ?? DEFAULT_CONFIDENCE_THRESHOLDS.fire,
        smoke: ct.smoke ?? ct.fire ?? DEFAULT_CONFIDENCE_THRESHOLDS.smoke,
        vehicle: ct.vehicle ?? DEFAULT_CONFIDENCE_THRESHOLDS.vehicle,
      });
      setPpeWornThresholds({ ...DEFAULT_PPE_WORN, ...ct.ppe.worn });
      setPpeViolationThresholds({ ...DEFAULT_PPE_VIOLATION, ...(ct.ppe.violation || {}) });
    } else {
      // Camera has no saved per-PPE thresholds yet.
      // Seed from the assigned detection profile (template), or fall back to built-in defaults.
      const profileConfig = camera?.detectionProfile?.config || null;
      if (profileConfig?.confidence_thresholds) {
        const ct = profileConfig.confidence_thresholds;
        setConfidenceThresholds({
          person: ct.person ?? DEFAULT_CONFIDENCE_THRESHOLDS.person,
          fire: ct.fire ?? DEFAULT_CONFIDENCE_THRESHOLDS.fire,
          smoke: ct.smoke ?? ct.fire ?? DEFAULT_CONFIDENCE_THRESHOLDS.smoke,
          vehicle: ct.vehicle ?? DEFAULT_CONFIDENCE_THRESHOLDS.vehicle,
        });
        const profilePpe = ct.ppe;
        if (profilePpe && typeof profilePpe === 'object') {
          setPpeWornThresholds({ ...DEFAULT_PPE_WORN, ...(profilePpe.worn || {}) });
          setPpeViolationThresholds({ ...DEFAULT_PPE_VIOLATION, ...(profilePpe.violation || {}) });
        } else {
          setPpeWornThresholds(DEFAULT_PPE_WORN);
          setPpeViolationThresholds(DEFAULT_PPE_VIOLATION);
        }
      } else {
        setConfidenceThresholds(DEFAULT_CONFIDENCE_THRESHOLDS);
        setPpeWornThresholds(DEFAULT_PPE_WORN);
        setPpeViolationThresholds(DEFAULT_PPE_VIOLATION);
      }
    }

    // Load remaining camera config (vehicle proximity, processing, advanced, analytics, zones)
    if (rawConfig) {
      const config = rawConfig;

      // Load confidence thresholds (person, fire, vehicle) from camera config if present and no saved PPE
      if (!hasSavedPpe && config.confidence_thresholds) {
        const ct = config.confidence_thresholds;
        setConfidenceThresholds(prev => ({
          ...prev,
          ...(ct.person !== undefined && { person: ct.person }),
          ...(ct.fire   !== undefined && { fire: ct.fire }),
          ...((ct.smoke ?? ct.fire) !== undefined && { smoke: ct.smoke ?? ct.fire }),
          ...(ct.vehicle !== undefined && { vehicle: ct.vehicle }),
        }));
      }

      // Load vehicle proximity settings
      if (config.vehicleProximity) {
        setVehicleProximitySettings(prev => ({ ...prev, ...config.vehicleProximity }));
      }

      // Load processing settings
      if (config.processing) {
        setProcessingSettings(prev => ({ ...prev, ...config.processing }));
      }

      // Load advanced thresholds
      if (config.advanced_thresholds) {
        setAdvancedThresholds(prev => ({ ...prev, ...config.advanced_thresholds }));
      }

      // Load PPE detection behaviour settings
      if (config.ppeDetection) {
        setPpeDetectionSettings(prev => ({ ...prev, ...config.ppeDetection }));
      }

      // Load alert cooldowns
      if (config.alertCooldowns) {
        setAlertCooldowns(prev => ({ ...prev, ...config.alertCooldowns }));
      }

      // Load crowd density settings
      if (config.crowdDensity) {
        setCrowdDensitySettings(prev => ({ ...prev, ...config.crowdDensity }));
      }

      // Load analytics with zone assignments
      if (config.analytics) {
        setAnalytics(config.analytics);
      } else if (config.enabledAnalytics) {
        // Migrate from old format
        setAnalytics(
          ANALYTICS_OPTIONS.map(opt => ({
            id: opt.id,
            enabled: config.enabledAnalytics.includes(opt.id),
            zones: ['all'],
          }))
        );
      }

      // Load stored reference image
      if (config.referenceImage) {
        setReferenceImage(config.referenceImage);
      }

      // Load zones from config
      const savedZones = Array.isArray(config.zones) ? config.zones : null;

      if (savedZones !== null) {
        setZones(savedZones);
      } else {
        setZones([]);
      }
    } else {
      setZones([]);
      setAnalytics(ANALYTICS_OPTIONS.map(opt => ({
        id: opt.id,
        enabled: false,
        zones: ['all'],
      })));
    }
  }, [camera?.id, id]);

  // Once zonesData (DB zones) resolves, patch any orphaned zones in state that have a
  // local temp id (zone_<timestamp>) but no dbId. This fixes the race condition where
  // the user saved the camera config before createZoneMutation completed.
  useEffect(() => {
    if (!zonesData || !Array.isArray(zonesData) || zonesData.length === 0) return;
    setZones(prev => {
      const hasOrphans = prev.some(z => !z.dbId && z.id?.startsWith('zone_'));
      if (!hasOrphans) return prev; // nothing to patch, skip re-render
      return prev.map(z => {
        if (!z.dbId && z.id?.startsWith('zone_')) {
          const match = zonesData.find(dbz => dbz.name === z.name);
          if (match) {
            return { ...z, dbId: match.id };
          }
        }
        return z;
      });
    });
  }, [zonesData]);

  // Drag handlers for vertex / whole-zone reshape on the camera preview
  useEffect(() => {
    if (!draggingVertex && !draggingZone) return;
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    const onMove = (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const x = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
      const y = clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100);

      if (draggingVertex) {
        setZones(prev => prev.map(z =>
          z.id === draggingVertex.zoneId
            ? { ...z, points: z.points.map((p, i) => i === draggingVertex.vertexIndex ? { x, y } : p) }
            : z
        ));
      } else if (draggingZone) {
        const dx = x - draggingZone.lastX;
        const dy = y - draggingZone.lastY;
        setZones(prev => prev.map(z =>
          z.id === draggingZone.zoneId
            ? { ...z, points: z.points.map(p => ({ x: clamp(p.x + dx, 0, 100), y: clamp(p.y + dy, 0, 100) })) }
            : z
        ));
        setDraggingZone(prev => prev ? { ...prev, lastX: x, lastY: y } : prev);
      }
    };
    const onUp = () => {
      setDraggingVertex(null);
      setDraggingZone(null);
      setHasChanges(true);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [draggingVertex, draggingZone]);

  // Connect to WebSocket on mount
  useEffect(() => {
    connect();
  }, [connect]);

  // Apply a fresh snapshot to the canvas ONLY when the user explicitly asked
  // for one. We used to auto-request a snapshot on every page mount and apply
  // whatever came back — that silently overwrote the saved reference image,
  // marked the config dirty, and made the canvas show a fresh capture every
  // time the user opened the page (so the user could never see their saved
  // reference). Now the saved referenceImage is the default; Refresh Snapshot
  // is the only path that swaps it.
  useEffect(() => {
    if (!isLoadingSnapshot) return;             // ignore stray frames
    if (!streamState?.snapshot) return;
    setReferenceImage(streamState.snapshot);
    setIsLoadingSnapshot(false);
    setSnapshotError(null);
    setHasChanges(true);
  }, [streamState?.snapshot, isLoadingSnapshot]);

  // Update camera mutation
  const createZoneMutation = useMutation({
    mutationFn: ({ zoneData }) => zonesAPI.createZone(zoneData),
    onSuccess: (response, { localId }) => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'zones',
      });
      // Replace the temp local ID with the real DB UUID so ZoneSelector
      // immediately has the correct ID without requiring a page reload.
      const savedZone = response?.data;
      if (savedZone?.id && localId) {
        setZones(prev => prev.map(z =>
          z.id === localId ? { ...z, id: savedZone.id, dbId: savedZone.id } : z
        ));
        setAnalytics(prev => prev.map(a => ({
          ...a,
          zones: a.zones.map(zid => zid === localId ? savedZone.id : zid),
        })));
      }
    },
    onError: (error) => {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
      console.error('Failed to create zone in database:', error);
      toast.error(`Zone save failed: ${msg}`);
    },
  });

  // Mutation for updating existing zone with polygon data
  const updateZoneMutation = useMutation({
    mutationFn: ({ zoneId, polygonData }) => zonesAPI.updateZone(zoneId, polygonData),
    onSuccess: () => {
      // Invalidate all zones queries to reflect the updated polygon in /zones page
      queryClient.invalidateQueries({
        predicate: (query) => {
          return query.queryKey[0] === 'zones';
        },
      });
      toast.success('Zone polygon saved to /zones page');
    },
    onError: (error) => {
      console.error('Failed to update zone in database:', error);
      toast.error('Zone polygon updated locally but failed to save to database');
    },
  });

  // Mutation for updating zone attributes (name/type/color/alertOnEntry) via /zones API
  const updateZoneAttrsMutation = useMutation({
    mutationFn: ({ zoneId, payload }) => zonesAPI.updateZone(zoneId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'zones',
      });
      toast.success('Zone saved to database');
    },
    onError: (error) => {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message;
      console.error('Failed to update zone attributes:', error);
      toast.error(`Zone DB save failed: ${msg}`);
    },
  });

  // Delete mutation for /zones endpoint
  const deleteZoneApiMutation = useMutation({
    mutationFn: (zoneId) => zonesAPI.deleteZone(zoneId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'zones',
      });
      toast.success('Zone deleted from database');
    },
    onError: (error) => {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message;
      toast.error(`Zone DB delete failed: ${msg}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data) => camerasAPI.updateCamera(id, data),
    onSuccess: () => {
      toast.success('Camera configuration saved');
      queryClient.invalidateQueries({ queryKey: ['camera', id] });
      queryClient.invalidateQueries({ queryKey: ['cameras'] });
      setHasChanges(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to save configuration');
    },
  });

  // Request a new snapshot
  const handleRefreshSnapshot = () => {
    if (camera?.aiDevice?.status !== 'online') {
      setSnapshotError('AI device is offline');
      return;
    }
    if (!connected) {
      connect();
      setSnapshotError('Connecting to server...');
      return;
    }
    setIsLoadingSnapshot(true);
    setSnapshotError(null);
    requestSnapshot(id, false);

    setTimeout(() => {
      if (isLoadingSnapshot) {
        setIsLoadingSnapshot(false);
        setSnapshotError('Snapshot request timed out');
      }
    }, 10000);
  };

  // Toggle analytics
  const toggleAnalytic = (analyticId) => {
    const option = ANALYTICS_OPTIONS.find(opt => opt.id === analyticId);
    const analyticState = analytics.find(a => a.id === analyticId);
    const capability = getAnalyticCapability(analyticId);

    if (!analyticState?.enabled) {
      if (!canEnableCapability(capability)) {
        toast.error(`${option?.name || 'This analytic'} needs a verified model before it can be enabled`);
        return;
      }
      if (capability.status === 'needs_zone' && option?.requiresZones && availableZones.length === 0) {
        toast.error(`${option.name} requires a zone on this camera first`);
        return;
      }
    }

    setAnalytics(prev => prev.map(a =>
      a.id === analyticId ? { ...a, enabled: !a.enabled } : a
    ));
    setHasChanges(true);
  };

  // Update analytic zones
  const updateAnalyticZones = (analyticId, newZones) => {
    setAnalytics(prev => prev.map(a =>
      a.id === analyticId ? { ...a, zones: newZones } : a
    ));
    setHasChanges(true);
  };

  // Point-in-polygon test (ray casting, % coordinate space)
  const pointInPolygon = (point, polygonPoints) => {
    if (!polygonPoints || polygonPoints.length < 3) return false;
    let inside = false;
    for (let i = 0, j = polygonPoints.length - 1; i < polygonPoints.length; j = i++) {
      const xi = polygonPoints[i].x, yi = polygonPoints[i].y;
      const xj = polygonPoints[j].x, yj = polygonPoints[j].y;
      const intersect = ((yi > point.y) !== (yj > point.y))
        && (point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // Canvas click — drawing mode adds a point; otherwise hit-tests zones and selects one.
  const handleCanvasClick = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (isDrawing) {
      setCurrentPoints(prev => [...prev, { x, y }]);
      return;
    }

    // Hit-test in reverse so the most-recently-drawn zone wins on overlap
    for (let i = zones.length - 1; i >= 0; i--) {
      const zone = zones[i];
      if (pointInPolygon({ x, y }, zone.points)) {
        setSelectedZone(prev => prev === zone.id ? null : zone.id);
        setActiveTab('zones');
        return;
      }
    }
    setSelectedZone(null);
  }, [isDrawing, zones]);

  const handleCompleteZone = () => {
    if (currentPoints.length < 3) {
      toast.error('A zone needs at least 3 points');
      return;
    }

    if (!newZoneName.trim()) {
      toast.error('Please enter a zone name');
      return;
    }

    // Check if we're updating an existing zone or creating a new one
    const existingZone = zones.find(z => z.id === selectedZone);
    const isUpdatingExisting = !!existingZone;

    if (isUpdatingExisting) {
      // Update existing zone's polygon
      const zone = zones.find(z => z.id === selectedZone);
      setZones(prev => prev.map(z =>
        z.id === selectedZone
          // Keep `polygon` in sync with `points` (single source = currentPoints) so the
          // stored zone never carries a stale polygon that disagrees with points.
          ? { ...z, points: currentPoints,
              polygon: { type: 'Polygon', coordinates: [currentPoints.map(p => [p.x, p.y])] } }
          : z
      ));
      setCurrentPoints([]);
      setNewZoneName('');
      setSelectedZone(null);
      setIsDrawing(false);
      setHasChanges(true);

      // Save polygon to database for existing zone
      if (zone?.dbId) {
        const coordinates = currentPoints.map(p => [p.x, p.y]);
        const polygonData = {
          polygon: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
        };
        updateZoneMutation.mutate({ zoneId: zone.dbId, polygonData });
      } else {
        toast.success('Zone polygon updated locally');
      }
    } else {
      // Create new zone
      const newZone = {
        id: `zone_${Date.now()}`,
        name: newZoneName,
        type: selectedZoneType,
        points: currentPoints,
        alertOnEntry: true,
      };

      setZones(prev => [...prev, newZone]);
      setCurrentPoints([]);
      setNewZoneName('');
      setIsDrawing(false);
      setHasChanges(true);
      toast.success('Zone added');

      // Also create the zone in the database so it appears in /zones page
      if (camera?.siteId) {
        const zonePayload = {
          siteId: camera.siteId,
          name: newZoneName,
          code: `${newZoneName.toUpperCase().replace(/\s+/g, '_')}_${Date.now()}`.substring(0, 50),
          type: selectedZoneType,
          polygon: {
            type: 'polygon',
            coordinates: [currentPoints.map(p => [p.x, p.y])],
          },
          alertOnEntry: true,
          alertOnPPE: true,
          alertOnOvercrowd: true,
          color: ZONE_TYPES.find(t => t.id === selectedZoneType)?.color || '#3B82F6',
        };

        createZoneMutation.mutate({ zoneData: zonePayload, localId: newZone.id });
      }
    }
  };

  const handleCancelDrawing = () => {
    setCurrentPoints([]);
    setIsDrawing(false);
  };

  // Check if a zone has a polygon (points) or polygon data
  const zoneHasPolygon = (zone) => {
    if (zone?.points && zone.points.length > 0) return true;
    if (zone?.polygon?.coordinates && zone.polygon.coordinates[0]?.length > 0) return true;
    return false;
  };

  // Convert polygon coordinates to points format
  const polygonToPoints = (polygon) => {
    if (!polygon?.coordinates || polygon.coordinates.length === 0) return [];
    const coords = polygon.coordinates[0] || []; // Get the first ring (outer boundary)
    return coords.map(([x, y]) => ({ x, y }));
  };

  // Handle drawing polygon for existing zone
  const handleDrawPolygonForZone = (zoneId) => {
    const zone = zones.find(z => z.id === zoneId);
    if (zone) {
      setSelectedZone(zoneId);
      setIsDrawing(true);
      // Use points if available, otherwise convert polygon to points
      const points = zone.points && zone.points.length > 0
        ? zone.points
        : polygonToPoints(zone.polygon);
      setCurrentPoints(points);
      setNewZoneName(zone.name);
      setSelectedZoneType(zone.type);
    }
  };

  // Handle zone assignment from modal (existing or new)
  const handleZoneAssigned = (zone) => {
    if (zone.source === 'existing') {
      // Zone selected from existing zones - convert polygon to points if needed
      let points = zone.points && zone.points.length > 0
        ? zone.points
        : polygonToPoints(zone.polygon);

      const newZone = {
        id: `zone_${Date.now()}`,
        ...zone,
        points, // Use converted points
        dbId: zone.dbId || zone.id,
      };
      setZones(prev => [...prev, newZone]);
      setHasChanges(true);
    } else if (zone.source === 'new') {
      // New zone created via modal drawing - add locally and create in DB
      setZones(prev => [...prev, zone]);
      setHasChanges(true);

      // Create the zone in database so it appears in /zones page
      if (camera?.siteId && zone.points && zone.points.length >= 3) {
        const coordinates = zone.points.map(p => [p.x, p.y]);
        const zonePayload = {
          siteId: camera.siteId,
          name: zone.name,
          code: `${zone.name.toUpperCase().replace(/\s+/g, '_')}_${Date.now()}`.substring(0, 50),
          type: zone.type,
          polygon: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
          alertOnEntry: zone.alertOnEntry || true,
          alertOnPPE: true,
          alertOnOvercrowd: true,
          color: ZONE_TYPES.find(t => t.id === zone.type)?.color || '#3B82F6',
          isActive: true,
        };

        createZoneMutation.mutate({ zoneData: zonePayload, localId: zone.id });
      } else if (camera?.siteId) {
        toast.error('Cannot save zone: insufficient points for polygon');
      }
    }
  };

  const handleDeleteZone = (zoneId) => {
    setZones(prev => prev.filter(z => z.id !== zoneId));
    // Also remove zone from analytics that reference it
    setAnalytics(prev => prev.map(a => ({
      ...a,
      zones: a.zones.filter(z => z !== zoneId).length > 0
        ? a.zones.filter(z => z !== zoneId)
        : ['all']
    })));
    setSelectedZone(null);
    setHasChanges(true);
    toast.success('Zone deleted');
  };

  // Update confidence threshold
  const updateConfidence = (key, value) => {
    setConfidenceThresholds(prev => ({
      ...prev,
      [key]: parseFloat(value),
    }));
    setHasChanges(true);
  };

  // Update per-PPE worn threshold
  const updatePpeWorn = (key, value) => {
    setPpeWornThresholds(prev => ({ ...prev, [key]: parseFloat(value) }));
    setHasChanges(true);
  };

  // Update per-PPE violation threshold
  const updatePpeViolation = (key, value) => {
    setPpeViolationThresholds(prev => ({ ...prev, [key]: parseFloat(value) }));
    setHasChanges(true);
  };

  // Reset confidence thresholds to defaults
  const resetConfidenceThresholds = () => {
    setConfidenceThresholds(DEFAULT_CONFIDENCE_THRESHOLDS);
    setPpeWornThresholds(DEFAULT_PPE_WORN);
    setPpeViolationThresholds(DEFAULT_PPE_VIOLATION);
    setHasChanges(true);
  };

  // Update a single advanced threshold value
  const updateAdvancedThreshold = (key, value) => {
    setAdvancedThresholds(prev => ({ ...prev, [key]: parseFloat(value) }));
    setHasChanges(true);
  };

  // Update a single PPE detection behaviour setting and mark the form dirty
  const updatePpeSetting = (key, value) => {
    setPpeDetectionSettings(p => ({ ...p, [key]: value }));
    setHasChanges(true);
  };

  // Reset advanced thresholds to defaults
  const resetAdvancedThresholds = () => {
    setAdvancedThresholds(DEFAULT_ADVANCED_THRESHOLDS);
    setPpeDetectionSettings(DEFAULT_PPE_DETECTION_SETTINGS);
    setHasChanges(true);
  };

  // Update vehicle proximity setting
  const updateVehicleProximity = (key, value) => {
    setVehicleProximitySettings(prev => ({
      ...prev,
      [key]: value,
    }));
    setHasChanges(true);
  };

  // Update processing setting
  const updateProcessing = (key, value) => {
    setProcessingSettings(prev => ({
      ...prev,
      [key]: value,
    }));
    setHasChanges(true);
  };

  // Save configuration
  const handleSave = () => {
    // Block save if zone DB creation is still in-flight (race condition guard)
    // Without this, the config would be persisted with a local temp ID (zone_<timestamp>)
    // and no dbId, causing CameraZone sync to skip the zone entirely.
    if (createZoneMutation.isPending) {
      toast.error('Please wait — zone is still being saved to the database');
      return;
    }

    // Inline recovery: for any zone that still has a local temp id and no dbId,
    // try to match it against zonesData by name and fill in dbId synchronously.
    // This is the authoritative fix — the useEffect recovery is best-effort only
    // and can lose the race if the user clicks Save before zonesData resolves.
    const dbZones = Array.isArray(zonesData) ? zonesData : [];
    const resolvedZones = zones.map(z => {
      if (!z.dbId && z.id?.startsWith('zone_')) {
        const match = dbZones.find(dbz => dbz.name === z.name);
        if (match) {
          return { ...z, dbId: match.id };
        }
      }
      return z;
    });

    const detectionConfig = {
      analytics,
      zones: resolvedZones,
      confidence_thresholds: {
        ...confidenceThresholds,
        // Persist an explicit smoke threshold (separate Smoke Detection slider).
        // Default to the fire value when the user hasn't diverged it, so the AI engine
        // always has a smoke threshold to gate the color-based smoke heuristic against.
        smoke: confidenceThresholds.smoke ?? confidenceThresholds.fire,
        // Save full per-item PPE structure — this is the source of truth for the AI engine
        ppe: {
          worn: ppeWornThresholds,
          violation: ppeViolationThresholds,
        },
      },
      vehicleProximity: vehicleProximitySettings,
      processing: processingSettings,
      advanced_thresholds: advancedThresholds,
      ppeDetection: ppeDetectionSettings,
      vestClassifierEnabled: !!ppeDetectionSettings.vestClassifierEnabled,
      vestClassifierShadow: ppeDetectionSettings.vestClassifierShadow ?? true,
      alertCooldowns,
      crowdDensity: crowdDensitySettings,
      referenceImage,
      updatedAt: new Date().toISOString(),
    };

    updateMutation.mutate({
      detectionConfig,
      detectionProfileId: selectedProfileId || null,
    });
  };

  // Draw zones on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw existing zones
    zones.forEach(zone => {
      const zoneType = ZONE_TYPES.find(t => t.id === zone.type);
      const color = zoneType?.color || '#3B82F6';

      // Handle both local zones (with points) and database zones (with polygon)
      const points = zone.points || [];
      if (points.length === 0) return; // Skip zones without points

      ctx.beginPath();
      points.forEach((point, i) => {
        const x = (point.x / 100) * canvas.width;
        const y = (point.y / 100) * canvas.height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.closePath();

      // When a zone is being edited, skip fill so the reference image stays
      // fully visible. Otherwise use a light tint so the polygon area is
      // discoverable without obscuring the underlying scene.
      const isSelected = selectedZone === zone.id;
      if (!isSelected) {
        ctx.fillStyle = `${color}1A`; // ~10% alpha
        ctx.fill();
      }

      ctx.strokeStyle = isSelected ? '#FFFFFF' : color;
      ctx.lineWidth = isSelected ? 3 : 2;
      if (isSelected) {
        // Glow effect so the selected outline pops without dimming the image
        ctx.shadowColor = color;
        ctx.shadowBlur = 8;
      }
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.shadowColor = 'transparent';

      // Draw zone name
      if (points.length > 0) {
        const centerX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
        const centerY = points.reduce((sum, p) => sum + p.y, 0) / points.length;
        const x = (centerX / 100) * canvas.width;
        const y = (centerY / 100) * canvas.height;

        ctx.font = '12px sans-serif';
        ctx.fillStyle = '#FFFFFF';
        ctx.textAlign = 'center';
        ctx.fillText(zone.name, x, y);
      }
    });

    // Draw current drawing points
    if (currentPoints.length > 0) {
      const zoneType = ZONE_TYPES.find(t => t.id === selectedZoneType);
      const color = zoneType?.color || '#3B82F6';

      ctx.beginPath();
      currentPoints.forEach((point, i) => {
        const x = (point.x / 100) * canvas.width;
        const y = (point.y / 100) * canvas.height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);

        // Draw point markers
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [zones, currentPoints, selectedZone, selectedZoneType]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!camera) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Camera not found</p>
      </div>
    );
  }

  const enabledCount = analytics.filter(a => a.enabled).length;
  const analyticGroups = [
    {
      title: 'Verified Local Models',
      description: 'These match the class names from the mounted PPE and fire/smoke weights.',
      options: ANALYTICS_OPTIONS.filter(opt => getAnalyticCapability(opt.id).status === 'supported'),
    },
    {
      title: 'Zone and Proximity Logic',
      description: 'These depend on camera zones or derived proximity logic.',
      options: ANALYTICS_OPTIONS.filter(opt => ['needs_zone', 'experimental'].includes(getAnalyticCapability(opt.id).status)),
    },
    {
      title: 'Needs Model Before Use',
      description: 'Keep disabled until a matching trained model is mounted and verified.',
      options: ANALYTICS_OPTIONS.filter(opt => getAnalyticCapability(opt.id).status === 'needs_model'),
    },
  ].filter(group => group.options.length > 0);

  return (
    <div className={embedded ? '' : 'space-y-6'}>
      {/* Header */}
      {!embedded && (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/cameras')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configure Camera</h1>
            <p className="text-gray-500">{camera.name} - {camera.location || 'No location'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-sm text-orange-600">Unsaved changes</span>
          )}
          <button
            onClick={() => navigate(`/cameras/${id}/training`)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <BeakerIcon className="w-4 h-4" />
            Training Data
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            className="btn btn-primary flex items-center gap-2"
          >
            {updateMutation.isPending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <CheckIcon className="w-4 h-4" />
            )}
            Save Configuration
          </button>
        </div>
      </div>
      )}

      {/* Embedded mode: compact header with camera name + save */}
      {embedded && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{camera.name}</h2>
            <p className="text-sm text-gray-500">{camera.location || 'No location'}</p>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <span className="text-xs text-orange-600">Unsaved</span>
            )}
            <button
              onClick={() => navigate(`/cameras/${id}/training`)}
              className="btn btn-secondary btn-sm flex items-center gap-2"
            >
              <BeakerIcon className="w-4 h-4" />
              Training Data
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending}
              className="btn btn-primary btn-sm flex items-center gap-2"
            >
              {updateMutation.isPending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <CheckIcon className="w-4 h-4" />
              )}
              Save
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Camera Preview */}
        <div className="lg:col-span-2">
          <div className="card overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Camera Preview</h3>
              {/* Hidden entirely for deleted cameras: the engine serves snapshots from a
                  running VideoProcessor's cached frame, and a deleted camera has no
                  processor — so the request can never succeed. A permanently disabled
                  button just invites clicks; the preview area explains the situation. */}
              {!deletedContext && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleRefreshSnapshot}
                    className="btn btn-secondary btn-sm flex items-center gap-1"
                    disabled={isLoadingSnapshot || camera.aiDevice?.status !== 'online'}
                  >
                    {isLoadingSnapshot ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                        Loading...
                      </>
                    ) : (
                      <>
                        <ArrowPathIcon className="w-4 h-4" />
                        Refresh Snapshot
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            <div
              ref={containerRef}
              className="relative aspect-video bg-gray-900"
            >
              {/* Reference image display */}
              {referenceImage ? (
                <img
                  src={referenceImage}
                  alt="Camera reference"
                  className="w-full h-full object-contain"
                />
              ) : snapshotFallback ? (
                <img
                  src={snapshotFallback}
                  alt="Last saved snapshot"
                  className="w-full h-full object-contain"
                />
              ) : isLoadingSnapshot ? (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p>Loading camera snapshot...</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <CameraIcon className="w-16 h-16 mx-auto mb-2" />
                    {snapshotError ? (
                      <>
                        <p className="text-red-400">{snapshotError}</p>
                        <p className="text-sm mt-1">Click "Refresh Snapshot" when device is online</p>
                      </>
                    ) : deletedContext ? (
                      <>
                        <p>No snapshot saved for this camera</p>
                        <p className="text-sm mt-1">Live preview is unavailable while the camera is deleted</p>
                      </>
                    ) : camera.aiDevice?.status === 'online' ? (
                      <p>Click "Refresh Snapshot" to load a camera preview</p>
                    ) : (
                      <>
                        <p>No camera preview available</p>
                        <p className="text-sm mt-1">AI device is offline</p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Zone drawing canvas overlay — always clickable so users can pick a zone by clicking on its polygon */}
              <canvas
                ref={canvasRef}
                className={clsx(
                  'absolute inset-0 w-full h-full pointer-events-auto',
                  isDrawing ? 'z-50 cursor-crosshair' : 'z-10 cursor-pointer'
                )}
                onClick={handleCanvasClick}
              />

              {/* Interactive vertex handles for the selected zone */}
              {!isDrawing && selectedZone && (() => {
                const zone = zones.find(z => z.id === selectedZone);
                if (!zone?.points?.length) return null;
                const zoneType = ZONE_TYPES.find(t => t.id === zone.type);
                const handleColor = zoneType?.color || '#3B82F6';
                return (
                  <div className="absolute inset-0 pointer-events-none z-20">
                    {zone.points.map((p, i) => (
                      <div
                        key={i}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDraggingVertex({ zoneId: zone.id, vertexIndex: i });
                        }}
                        className="absolute pointer-events-auto cursor-grab active:cursor-grabbing"
                        style={{
                          left: `${p.x}%`,
                          top: `${p.y}%`,
                          width: 14,
                          height: 14,
                          marginLeft: -7,
                          marginTop: -7,
                          borderRadius: '50%',
                          background: 'white',
                          border: `2px solid ${handleColor}`,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                        }}
                        title={`Vertex ${i + 1}`}
                      />
                    ))}
                  </div>
                );
              })()}

              {/* Drag-mode hint */}
              {!isDrawing && selectedZone && (
                <div className="absolute top-2 left-2 bg-blue-600/90 text-white px-3 py-1 rounded-full text-xs flex items-center gap-2 z-30">
                  <span>Drag any corner to reshape the polygon</span>
                </div>
              )}

              {/* Drawing mode indicator */}
              {isDrawing && (
                <div className="absolute top-2 left-2 bg-red-600 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  Drawing Zone - Click to add points
                </div>
              )}

              {/* Reference image info badge */}
              {referenceImage && !isDrawing && (
                <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                  Reference Image
                </div>
              )}
            </div>

            {/* Zone drawing controls - always visible */}
            <div className="p-4 bg-gray-50 border-t">
              {!isDrawing ? (
                <button
                  onClick={() => setShowZoneAssignmentModal(true)}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <PlusIcon className="w-4 h-4" />
                  Assign/Create Zone
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={newZoneName}
                    onChange={(e) => setNewZoneName(e.target.value)}
                    placeholder="Zone name..."
                    className="input flex-1"
                  />
                  <span className="text-sm text-gray-500">
                    {currentPoints.length} points
                  </span>
                  <button
                    onClick={() => setCurrentPoints([])}
                    disabled={currentPoints.length === 0}
                    className="btn btn-secondary flex items-center gap-2"
                    title="Clear all points"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Clear
                  </button>
                  <button
                    onClick={handleCompleteZone}
                    disabled={currentPoints.length < 3}
                    className="btn btn-success flex items-center gap-2"
                  >
                    <CheckIcon className="w-4 h-4" />
                    Complete
                  </button>
                  <button
                    onClick={handleCancelDrawing}
                    className="btn btn-secondary flex items-center gap-2"
                  >
                    <XMarkIcon className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Unified Configuration Panel */}
        <div className="flex flex-col max-h-[calc(100vh-200px)]">
          {/* Tab Bar */}
          <div className="card mb-3 flex-shrink-0 overflow-hidden">
            <div className="flex border-b">
              {CONFIG_TABS.map(tab => {
                const TabIcon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    title={tab.label}
                    className={clsx(
                      'flex-1 flex flex-col items-center gap-0.5 px-1 py-2 text-[11px] font-medium border-b-2 transition-colors',
                      isActive
                        ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    <TabIcon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-4 overflow-y-auto flex-1">
          {/* Zone Management Section */}
          {activeTab === 'zones' && (
          <div className="card">
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <MapPinIcon className="w-5 h-5" />
                Zones ({zones.length})
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Define detection areas on the camera view
              </p>
            </div>
            {zones.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                <MapPinIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>No zones defined</p>
                <p className="text-xs">Use "Draw New Zone" below the preview</p>
              </div>
            ) : (
              <div className="divide-y">
                {zones.map(zone => {
                  const zoneType = ZONE_TYPES.find(t => t.id === zone.type);
                  return (
                    <div
                      key={zone.id}
                      className={clsx(
                        'p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50',
                        selectedZone === zone.id && 'bg-blue-50'
                      )}
                      onClick={() => setSelectedZone(selectedZone === zone.id ? null : zone.id)}
                    >
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: zoneType?.color || '#3B82F6' }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{zone.name}</p>
                        <p className="text-xs text-gray-500">{zoneType?.name}</p>
                      </div>
                      {!zoneHasPolygon(zone) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDrawPolygonForZone(zone.id);
                          }}
                          className="p-1.5 text-amber-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                          title="Draw polygon for this zone"
                        >
                          <PlusIcon className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteZone(zone.id);
                        }}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {/* Zone Details Panel — populated when a zone is selected */}
          {activeTab === 'zones' && selectedZone && (() => {
            const zone = zones.find(z => z.id === selectedZone);
            if (!zone) return null;
            const zoneType = ZONE_TYPES.find(t => t.id === zone.type);
            const linkedAnalytics = ANALYTICS_OPTIONS.filter(opt => {
              const a = analytics.find(x => x.id === opt.id);
              if (!a || !a.enabled) return false;
              return a.zones?.includes(zone.id) || a.zones?.includes(zone.dbId);
            });

            const updateZone = (patch) => {
              setZones(prev => prev.map(z => z.id === selectedZone ? { ...z, ...patch } : z));
              setHasChanges(true);
            };

            const dbId = zone.dbId || (zone.id && !zone.id.startsWith('zone_') ? zone.id : null);
            const handleSaveToDb = () => {
              if (!dbId) {
                toast.error('Zone has no DB record yet — click "Save Configuration" first');
                return;
              }
              updateZoneAttrsMutation.mutate({
                zoneId: dbId,
                payload: {
                  name: zone.name,
                  type: zone.type,
                  alertOnEntry: !!zone.alertOnEntry,
                  color: ZONE_TYPES.find(t => t.id === zone.type)?.color || '#3B82F6',
                },
              });
            };

            return (
              <div className="card border-2 border-blue-300">
                <div className="p-4 border-b bg-blue-50 flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: zoneType?.color || '#3B82F6' }} />
                    <h3 className="font-semibold text-gray-900 truncate">Zone Settings</h3>
                  </div>
                  <button
                    onClick={() => setSelectedZone(null)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                    title="Close"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-4 space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={zone.name || ''}
                      onChange={(e) => updateZone({ name: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Zone Type</label>
                    <div className="grid grid-cols-2 gap-2">
                      {ZONE_TYPES.map(t => (
                        <button
                          key={t.id}
                          onClick={() => updateZone({ type: t.id })}
                          className={clsx(
                            'flex items-center gap-2 px-2 py-1.5 text-xs rounded-lg border transition-colors text-left',
                            zone.type === t.id
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300 text-gray-700'
                          )}
                        >
                          <span className="w-2.5 h-2.5 rounded flex-shrink-0" style={{ backgroundColor: t.color }} />
                          <span className="truncate">{t.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Alert on Entry */}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Alert on Entry</p>
                      <p className="text-xs text-gray-500">Trigger alert when a person enters this zone</p>
                    </div>
                    <button
                      onClick={() => updateZone({ alertOnEntry: !zone.alertOnEntry })}
                      className={clsx(
                        'w-10 h-6 rounded-full transition-colors flex-shrink-0',
                        zone.alertOnEntry ? 'bg-blue-600' : 'bg-gray-200'
                      )}
                    >
                      <div className={clsx(
                        'w-5 h-5 bg-white rounded-full shadow transform transition-transform mt-0.5',
                        zone.alertOnEntry ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'
                      )} />
                    </button>
                  </div>

                  {/* Polygon status */}
                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Polygon</p>
                        <p className="text-xs text-gray-500">
                          {zoneHasPolygon(zone)
                            ? `${(zone.points || []).length} points defined`
                            : 'No polygon drawn yet'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDrawPolygonForZone(zone.id)}
                        className="btn btn-secondary btn-sm"
                      >
                        {zoneHasPolygon(zone) ? 'Redraw' : 'Draw'}
                      </button>
                    </div>
                  </div>

                  {/* Linked Analytics */}
                  <div className="pt-2 border-t">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Linked Analytics ({linkedAnalytics.length})
                    </p>
                    {linkedAnalytics.length === 0 ? (
                      <p className="text-xs text-gray-500">
                        No analytics target this zone. Enable an analytic in the Analytics tab and set "Apply to" to include this zone.
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {linkedAnalytics.map(opt => {
                          const Icon = opt.icon;
                          return (
                            <div key={opt.id} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded">
                              <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              <span className="text-xs text-gray-700 flex-1 truncate">{opt.name}</span>
                              <span className={clsx(
                                'text-[10px] px-1.5 py-0.5 rounded font-medium',
                                opt.severity === 'CRITICAL' && 'bg-red-100 text-red-700',
                                opt.severity === 'HIGH' && 'bg-orange-100 text-orange-700',
                                opt.severity === 'MEDIUM' && 'bg-yellow-100 text-yellow-700',
                                opt.severity === 'LOW' && 'bg-blue-100 text-blue-700',
                              )}>
                                {opt.severity}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* DB persistence actions */}
                  <div className="pt-3 border-t flex items-center gap-2">
                    <button
                      onClick={handleSaveToDb}
                      disabled={!dbId || updateZoneAttrsMutation.isPending}
                      className="btn btn-primary btn-sm flex-1 flex items-center justify-center gap-2"
                      title={dbId ? 'Persist name/type/alert to /zones DB' : 'Zone has no DB record yet'}
                    >
                      {updateZoneAttrsMutation.isPending ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <CheckIcon className="w-4 h-4" />
                      )}
                      Save to /zones DB
                    </button>
                    <button
                      onClick={() => {
                        if (!dbId) {
                          handleDeleteZone(zone.id);
                          return;
                        }
                        if (window.confirm(`Delete zone "${zone.name}" from the database?\n\nThis removes it from /zones page and any cameras using it.`)) {
                          deleteZoneApiMutation.mutate(dbId, {
                            onSuccess: () => {
                              handleDeleteZone(zone.id);
                              setSelectedZone(null);
                            },
                          });
                        }
                      }}
                      disabled={deleteZoneApiMutation.isPending}
                      className="btn btn-sm bg-red-100 text-red-700 hover:bg-red-200 flex items-center gap-2 px-3"
                      title="Delete zone from database"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                  {!dbId && (
                    <p className="text-xs text-amber-600 -mt-2">
                      ⚠️ Zone not yet persisted to /zones DB. Click "Save Configuration" at the top first to create the DB record.
                    </p>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Detection Profile Selector */}
          {activeTab === 'detection' && (
            <div className={clsx('card border-l-4', cameraHasSavedPpe ? 'bg-gray-50 border-l-gray-300' : 'bg-blue-50 border-l-blue-500')}>
                <div className="p-4">
                  <label className="block text-sm font-semibold text-gray-900 mb-1">
                    {cameraHasSavedPpe ? 'Load Defaults from Profile' : 'Detection Profile'}
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    {cameraHasSavedPpe
                      ? 'This camera has custom per-PPE thresholds saved. Selecting a profile will reset the PPE sliders to that profile\'s default values.'
                      : 'Select a profile to load its default PPE thresholds into the sliders below. You can then adjust each item individually before saving.'}
                  </p>
                  <select
                    value={selectedProfileId || ''}
                    onChange={(e) => {
                      const profileId = e.target.value || null;
                      setSelectedProfileId(profileId);
                      setHasChanges(true);

                      if (profileId) {
                        const profile = profiles.find(p => p.id === profileId);
                        if (profile?.config) {
                          const ct = profile.config.confidence_thresholds || {};
                          // Always apply profile's general thresholds
                          setConfidenceThresholds({
                            person: ct.person ?? DEFAULT_CONFIDENCE_THRESHOLDS.person,
                            fire: ct.fire ?? DEFAULT_CONFIDENCE_THRESHOLDS.fire,
                            smoke: ct.smoke ?? ct.fire ?? DEFAULT_CONFIDENCE_THRESHOLDS.smoke,
                            vehicle: ct.vehicle ?? DEFAULT_CONFIDENCE_THRESHOLDS.vehicle,
                          });
                          // Apply profile's per-PPE thresholds to sliders
                          const profilePpe = ct.ppe;
                          if (profilePpe && typeof profilePpe === 'object') {
                            setPpeWornThresholds({ ...DEFAULT_PPE_WORN, ...(profilePpe.worn || {}) });
                            setPpeViolationThresholds({ ...DEFAULT_PPE_VIOLATION, ...(profilePpe.violation || {}) });
                          } else {
                            setPpeWornThresholds(DEFAULT_PPE_WORN);
                            setPpeViolationThresholds(DEFAULT_PPE_VIOLATION);
                          }
                          setVehicleProximitySettings(prev => ({ ...prev, ...(profile.config.vehicleProximity || {}) }));
                          setProcessingSettings(prev => ({ ...prev, ...(profile.config.processing || {}) }));
                          toast.success(`PPE thresholds loaded from: ${profile.displayName}`);
                        }
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="">-- Select a profile --</option>
                    {profiles.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.displayName} ({p.industry}){p.isDefault ? ' (Default)' : ''}
                      </option>
                    ))}
                  </select>
                  {cameraHasSavedPpe && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <span>Custom thresholds are active. Selecting a profile above will overwrite them in the sliders (not saved until you click Save).</span>
                    </p>
                  )}
                </div>
              </div>
          )}

          {/* Confidence Thresholds Section */}
          {activeTab === 'detection' && (
          <div className="card">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <AdjustmentsHorizontalIcon className="w-5 h-5" />
                  Detection Confidence
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Adjust thresholds to reduce false positives
                </p>
              </div>
              <button
                onClick={resetConfidenceThresholds}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Reset to Defaults
              </button>
            </div>
            <div className="p-4 space-y-5">
              {/* Person Detection Confidence */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Person Detection
                  </label>
                  <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                    {Math.round(confidenceThresholds.person * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0.3"
                  max="0.9"
                  step="0.05"
                  value={confidenceThresholds.person}
                  onChange={(e) => updateConfidence('person', e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>More Detections</span>
                  <span>Fewer False Positives</span>
                </div>
              </div>


              {/* Fire Detection Confidence */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Fire Detection
                  </label>
                  <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                    {Math.round(confidenceThresholds.fire * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0.1"
                  max="0.9"
                  step="0.05"
                  value={confidenceThresholds.fire}
                  onChange={(e) => updateConfidence('fire', e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>More Detections</span>
                  <span>Fewer False Positives</span>
                </div>
              </div>

              {/* Smoke Detection Confidence */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Smoke Detection
                  </label>
                  <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                    {Math.round((confidenceThresholds.smoke ?? confidenceThresholds.fire) * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0.1"
                  max="0.9"
                  step="0.05"
                  value={confidenceThresholds.smoke ?? confidenceThresholds.fire}
                  onChange={(e) => updateConfidence('smoke', e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>More Detections</span>
                  <span>Fewer False Positives</span>
                </div>
              </div>

              {/* Vehicle Detection Confidence */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Vehicle Detection
                  </label>
                  <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                    {Math.round(confidenceThresholds.vehicle * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="0.95"
                  step="0.05"
                  value={confidenceThresholds.vehicle}
                  onChange={(e) => updateConfidence('vehicle', e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>More Detections</span>
                  <span>Fewer False Positives</span>
                </div>
              </div>

              <div className="pt-2 border-t">
                <p className="text-xs text-gray-500">
                  Lower thresholds detect more objects but may increase false positives.
                  Higher thresholds are more accurate but may miss some detections.
                </p>
              </div>
            </div>
          </div>
          )}

          {/* Processing Settings Section */}
          {activeTab === 'processing' && (
          <div className="card">
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <BoltIcon className="w-5 h-5" />
                Processing Settings
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Control processing rate and priority
              </p>
            </div>
            <div className="p-4 space-y-5">
              {/* Target FPS */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <ClockIcon className="w-4 h-4" />
                    Target FPS
                  </label>
                  <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                    {processingSettings.targetFps} fps
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="15"
                  step="1"
                  value={processingSettings.targetFps}
                  onChange={(e) => updateProcessing('targetFps', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1 fps (Low resource)</span>
                  <span>15 fps (High detail)</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Higher FPS means more frames analyzed per second, using more GPU/CPU resources.
                  For 50 cameras on RTX 3060, recommend 1-2 fps per camera.
                </p>
              </div>

              {/* Priority */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Processing Priority
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PRIORITY_OPTIONS.map(option => (
                    <button
                      key={option.id}
                      onClick={() => updateProcessing('priority', option.id)}
                      className={clsx(
                        'p-3 rounded-lg border-2 text-left transition-all',
                        processingSettings.priority === option.id
                          ? option.color === 'red' ? 'border-red-500 bg-red-50'
                          : option.color === 'orange' ? 'border-orange-500 bg-orange-50'
                          : option.color === 'blue' ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-500 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <p className={clsx(
                        'font-medium text-sm',
                        processingSettings.priority === option.id
                          ? option.color === 'red' ? 'text-red-700'
                          : option.color === 'orange' ? 'text-orange-700'
                          : option.color === 'blue' ? 'text-blue-700'
                          : 'text-gray-700'
                          : 'text-gray-700'
                      )}>
                        {option.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-2 border-t">
                <p className="text-xs text-gray-500">
                  Critical cameras are processed first and maintain frame rate during high load.
                  Low priority cameras may have reduced FPS when system is busy.
                </p>
              </div>
            </div>
          </div>
          )}

          {/* Advanced Settings */}
          {activeTab === 'advanced' && (<>
          <SettingsSearchBar value={advancedSearch} onChange={setAdvancedSearch} placeholder="Search fire, vest, smoke, fall…" />

          <SettingsCard
            title="Advanced Detection Thresholds"
            description="Fine-tune fire, smoke, and fall detection sensitivity for this camera"
            icon={AdjustmentsHorizontalIcon}
            open={showAdvancedThresholds}
            onToggle={() => setShowAdvancedThresholds(v => !v)}
            searchActive={advancedSearchActive}
            query={advancedSearch}
          >
                <div className="flex justify-end">
                  <button onClick={resetAdvancedThresholds} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                    Reset to Defaults
                  </button>
                </div>

                <SettingsGroup title="Fire Detection" accent="orange" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(advancedThresholds, DEFAULT_ADVANCED_THRESHOLDS, ['fire_high_confidence_threshold', 'fire_min_area_ratio', 'fire_max_area_ratio', 'fire_max_aspect_ratio', 'fire_person_overlap_threshold', 'fire_ppe_secondary_confidence', 'fire_proximity_distance_px', 'fire_color_max_area_ratio', 'fire_min_bg_deviation', 'fire_min_motion_ratio', 'fire_min_texture_std']) + countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['firePersonOverlapCheck'])}
                >
                  <SliderRow label="Fire Instant-Alert Confidence" accent="orange"
                    value={advancedThresholds.fire_high_confidence_threshold} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.fire_high_confidence_threshold}
                    min="0.1" max="0.99" step="0.01" format={v => `${Math.round(v * 100)}%`}
                    onChange={e => updateAdvancedThreshold('fire_high_confidence_threshold', e.target.value)}
                    description="Fire detections above this confidence trigger immediately (skip multi-frame check). Default: 85%." />
                  <SliderRow label="Fire Min Detection Size" accent="orange"
                    value={advancedThresholds.fire_min_area_ratio} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.fire_min_area_ratio}
                    min="0.001" max="0.02" step="0.001" format={v => `${(v * 100).toFixed(2)}%`}
                    minLabel="0.1% (catches small sparks)" maxLabel="2% (large fires only)"
                    onChange={e => updateAdvancedThreshold('fire_min_area_ratio', e.target.value)}
                    description="Minimum fire bounding box as % of frame. Reduces false positives from small red objects. Default: 0.3%." />
                  <SliderRow label="Fire Max Detection Size" accent="orange"
                    value={advancedThresholds.fire_max_area_ratio} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.fire_max_area_ratio}
                    min="0.10" max="0.80" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="10% (reject large boxes)" maxLabel="80% (allow big fires)"
                    onChange={e => updateAdvancedThreshold('fire_max_area_ratio', e.target.value)}
                    description="Maximum fire bounding box as % of frame. Boxes larger than this are rejected as walls/vehicles/lighting. Default: 30%." />
                  <SliderRow label="Fire Max Width:Height Ratio" accent="orange"
                    value={advancedThresholds.fire_max_aspect_ratio} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.fire_max_aspect_ratio}
                    min="1.0" max="5.0" step="0.1" format={v => `${Number(v).toFixed(1)}×`}
                    minLabel="1.0 (tall/square flames only)" maxLabel="5.0 (wide flames allowed)"
                    onChange={e => updateAdvancedThreshold('fire_max_aspect_ratio', e.target.value)}
                    description="Rejects wide flat bboxes (hazard tape, road markings). Default: 2.5×." />
                  <SliderRow label="Fire-Person Overlap Suppress" accent="orange"
                    value={advancedThresholds.fire_person_overlap_threshold} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.fire_person_overlap_threshold}
                    min="0.50" max="1.0" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="50% (aggressive suppress)" maxLabel="100% (rarely suppress)"
                    onChange={e => updateAdvancedThreshold('fire_person_overlap_threshold', e.target.value)}
                    description="Suppress a fire detection when this % of its box overlaps a person (avoids flagging hi-vis vests as fire). Default: 85%." />
                  <ToggleRow accent="orange" label="Enable fire-person overlap suppression"
                    checked={ppeDetectionSettings.firePersonOverlapCheck ?? true}
                    onChange={e => updatePpeSetting('firePersonOverlapCheck', e.target.checked)}
                    description="When enabled, suppresses fire detections that heavily overlap a detected person (avoids hi-vis vest false fires). Disable for cameras where real fires near workers must always alert." />
                  <SliderRow label="Fire PPE Secondary Confidence" accent="orange"
                    value={advancedThresholds.fire_ppe_secondary_confidence} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.fire_ppe_secondary_confidence}
                    min="0.1" max="1.0" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="10% (strict suppression)" maxLabel="100% (disable suppression)"
                    onChange={e => updateAdvancedThreshold('fire_ppe_secondary_confidence', e.target.value)}
                    description="Minimum PPE confidence required to suppress a fire detection as false positive. Raise to 90%+ when smoke near workers is not being detected. Default: 30%." />
                  <SliderRow label="Fire Proximity Distance" accent="orange"
                    value={advancedThresholds.fire_proximity_distance_px ?? 200} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.fire_proximity_distance_px}
                    min="50" max="600" step="10" format={v => `${v} px`}
                    minLabel="50px (close only)" maxLabel="600px (wide)"
                    onChange={e => updateAdvancedThreshold('fire_proximity_distance_px', e.target.value)}
                    description="Max person-to-fire centre distance (in pixels) to raise a fire-proximity alert. This is FOV-dependent — set higher on wide/zoomed-out cameras, lower on tight ones. Default: 200px." />
                  <SliderRow label="Colour-Fire Max Blob Size" accent="orange"
                    value={advancedThresholds.fire_color_max_area_ratio ?? 0.70} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.fire_color_max_area_ratio}
                    min="0.10" max="1.00" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="10% (reject large blobs)" maxLabel="100% (allow any size)"
                    onChange={e => updateAdvancedThreshold('fire_color_max_area_ratio', e.target.value)}
                    description="Maximum size of a colour-based fire blob as % of frame — anything larger is rejected as implausible (walls, painted surfaces). Default: 70%; lower to reject more large blobs." />
                  <SliderRow label="Colour-Fire Min Background Deviation" accent="orange"
                    value={advancedThresholds.fire_min_bg_deviation ?? 20} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.fire_min_bg_deviation}
                    min="5" max="50" step="1"
                    minLabel="5 (lenient)" maxLabel="50 (strict)"
                    onChange={e => updateAdvancedThreshold('fire_min_bg_deviation', e.target.value)}
                    description="Minimum mean deviation from the background model for a colour-fire blob — static bollards/barriers match the background and are rejected. Default: 20; raise to cut false positives, lower if slow-burning fires are missed." />
                  <SliderRow label="Colour-Fire Min Motion Ratio" accent="orange"
                    value={advancedThresholds.fire_min_motion_ratio ?? 0.15} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.fire_min_motion_ratio}
                    min="0.00" max="0.50" step="0.01" format={v => Number(v).toFixed(2)}
                    minLabel="0 (no flicker required)" maxLabel="0.50 (strong flicker)"
                    onChange={e => updateAdvancedThreshold('fire_min_motion_ratio', e.target.value)}
                    description="Minimum fraction of a colour-fire blob showing frame-to-frame flicker. Default: 0.15 (real fire ~0.26, static objects <0.03); lower catches steadier flames, raise to reject moving machinery." />
                  <SliderRow label="Colour-Fire Min Texture" accent="orange"
                    value={advancedThresholds.fire_min_texture_std ?? 15} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.fire_min_texture_std}
                    min="5" max="40" step="1"
                    minLabel="5 (lenient)" maxLabel="40 (strict)"
                    onChange={e => updateAdvancedThreshold('fire_min_texture_std', e.target.value)}
                    description="Minimum grayscale texture variation (std dev) inside a colour-fire blob — uniform lights/painted objects score low and are rejected. Default: 15; raise to cut false positives from flat bright objects." />
                </SettingsGroup>

                <SettingsGroup title="Fire Colour Detection" accent="orange" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['fireColorDetectionEnabled', 'fireColorCheckEnabled', 'fireColorMinRatio', 'fireHueMinStd', 'fireVMinStd', 'fireYellowMinRatio', 'fireRedCoreMinRatio', 'fireColorMinRatioSmall', 'fireRequiredConsecutive'])}
                >
                  <ToggleRow accent="orange" label="Fire colour verification gate"
                    checked={ppeDetectionSettings.fireColorCheckEnabled ?? true}
                    onChange={e => updatePpeSetting('fireColorCheckEnabled', e.target.checked)}
                    description="Verifies every fire detection has real flame colours (hue diversity, brightness variation, yellow highlights, red core) before alerting. Keep ON for live cameras — it rejects orange vests and warm lighting. Turn OFF only for test cameras streaming recorded/compressed video where compression destroys the flame colour signature." />
                  <ToggleRow accent="orange" label="Enable fire colour detection"
                    checked={ppeDetectionSettings.fireColorDetectionEnabled ?? false}
                    onChange={e => updatePpeSetting('fireColorDetectionEnabled', e.target.checked)}
                    description="ADDS fire/smoke detections found by HSV colour analysis alone, on top of whatever the fire model reports — colour hits that don't overlap a model detection are raised as their own alerts. This can only INCREASE alerts, so leave it OFF unless the fire model is unavailable for this camera. To reduce false fires from red/orange objects or warm lighting, use 'Fire colour verification gate' below instead — that one filters the model's detections rather than adding to them." />
                  <SliderRow label="Fire Colour Min Ratio" accent="orange"
                    value={ppeDetectionSettings.fireColorMinRatio ?? 0.15} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.fireColorMinRatio}
                    min="0.05" max="0.80" step="0.05" format={v => Number(v).toFixed(2)} minLabel="0.05" maxLabel="0.80"
                    onChange={e => updatePpeSetting('fireColorMinRatio', parseFloat(e.target.value))}
                    description="Minimum fraction of the fire bounding box that must show fire-like colour to confirm the detection. Only applies when fire colour detection is enabled above." />
                  <SliderRow label="Fire Hue Diversity Min" accent="orange"
                    value={ppeDetectionSettings.fireHueMinStd ?? 11} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.fireHueMinStd}
                    min="3" max="15" step="1" minLabel="3" maxLabel="15"
                    onChange={e => updatePpeSetting('fireHueMinStd', parseFloat(e.target.value))}
                    description="Minimum hue variation (std dev) the fire bounding box must have to pass colour verification. Default 11 rejects orange vests/hats. Lower to 5–6 for cameras using recorded footage where video compression reduces colour spread." />
                  <SliderRow label="Fire Brightness Variation Min" accent="orange"
                    value={ppeDetectionSettings.fireVMinStd ?? 22} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.fireVMinStd}
                    min="5" max="40" step="1" minLabel="5" maxLabel="40"
                    onChange={e => updatePpeSetting('fireVMinStd', parseFloat(e.target.value))}
                    description="Minimum brightness variation (V std dev) in fire-coloured pixels — real flames have a glowing core fading to dark edges, uniform PPE surfaces do not. Default 22; lower it if dim/compressed fires are being rejected, raise it to reject more reflective PPE." />
                  <SliderRow label="Fire Yellow Component Min" accent="orange"
                    value={ppeDetectionSettings.fireYellowMinRatio ?? 0.035} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.fireYellowMinRatio}
                    min="0.00" max="0.20" step="0.005" format={v => Number(v).toFixed(3)} minLabel="0.000" maxLabel="0.200"
                    onChange={e => updatePpeSetting('fireYellowMinRatio', parseFloat(e.target.value))}
                    description="Minimum fraction of the crop that must be yellow-orange (flame tips are always yellow). Default 0.035; lower it if deep-red fires are missed, raise it to reject pure red/orange equipment." />
                  <SliderRow label="Fire Red Core Min" accent="orange"
                    value={ppeDetectionSettings.fireRedCoreMinRatio ?? 0.02} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.fireRedCoreMinRatio}
                    min="0.00" max="0.10" step="0.005" format={v => Number(v).toFixed(3)} minLabel="0.000" maxLabel="0.100"
                    onChange={e => updatePpeSetting('fireRedCoreMinRatio', parseFloat(e.target.value))}
                    description="Minimum fraction of the crop that must be deep red (every real flame has a hot red base; yellow/orange PPE has none). Default 0.02; lower it if small/early fires are missed, raise it to reject orange objects more aggressively." />
                  <SliderRow label="Fire Colour Min Ratio (Small)" accent="orange"
                    value={ppeDetectionSettings.fireColorMinRatioSmall ?? 0.05} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.fireColorMinRatioSmall}
                    min="0.00" max="0.50" step="0.01" format={v => Number(v).toFixed(2)} minLabel="0.00" maxLabel="0.50"
                    onChange={e => updatePpeSetting('fireColorMinRatioSmall', parseFloat(e.target.value))}
                    description="Minimum fire-colour pixel ratio for the initial colour check on model fire detections (small/normal boxes). Default 0.05; raise it to reject boxes with only a sliver of fire colour, lower it if partial flames are being rejected." />
                  <SliderRow label="Fire Required Consecutive Frames" accent="orange"
                    value={ppeDetectionSettings.fireRequiredConsecutive ?? 3} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.fireRequiredConsecutive}
                    min="1" max="10" step="1" minLabel="1" maxLabel="10"
                    onChange={e => updatePpeSetting('fireRequiredConsecutive', parseInt(e.target.value))}
                    description="Number of consecutive frames a fire must be detected in before an alert fires. Higher values cut false positives from brief flashes but add latency to real alerts." />
                </SettingsGroup>

                <SettingsGroup title="Smoke Detection Filters" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['smokeColorMinRatio', 'smokeLargeBypassThreshold', 'smokeSkyExclusionRatio', 'smokeAreaRatioMin', 'smokeAreaRatioMax'])}
                >
                  <SliderRow label="Smoke Colour Min Ratio" accent="blue"
                    value={ppeDetectionSettings.smokeColorMinRatio ?? 0.10} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.smokeColorMinRatio}
                    min="0.05" max="0.50" step="0.05" format={v => Number(v).toFixed(2)} minLabel="0.05" maxLabel="0.50"
                    onChange={e => updatePpeSetting('smokeColorMinRatio', parseFloat(e.target.value))}
                    description="Minimum fraction of the smoke bounding box that must show smoke-like grey/white colour to confirm the detection." />
                  <SliderRow label="Smoke Large Bypass Threshold" accent="blue"
                    value={ppeDetectionSettings.smokeLargeBypassThreshold ?? 0.30} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.smokeLargeBypassThreshold}
                    min="0.10" max="0.60" step="0.05" format={v => Number(v).toFixed(2)} minLabel="0.10" maxLabel="0.60"
                    onChange={e => updatePpeSetting('smokeLargeBypassThreshold', parseFloat(e.target.value))}
                    description="Smoke boxes larger than this fraction of the frame skip the colour/texture checks entirely and are treated as smoke directly — for dense smoke that fills most of the view." />
                  <SliderRow label="Smoke Sky Exclusion Ratio" accent="blue"
                    value={ppeDetectionSettings.smokeSkyExclusionRatio ?? 0.30} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.smokeSkyExclusionRatio}
                    min="0.10" max="0.50" step="0.05" format={v => Number(v).toFixed(2)} minLabel="0.10" maxLabel="0.50"
                    onChange={e => updatePpeSetting('smokeSkyExclusionRatio', parseFloat(e.target.value))}
                    description="Detections in the top fraction of the frame (the sky) are excluded, to stop clouds or haze near the horizon from being read as smoke." />
                  <SliderRow label="Smoke Min Area Ratio" accent="blue"
                    value={ppeDetectionSettings.smokeAreaRatioMin ?? 0.02} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.smokeAreaRatioMin}
                    min="0.01" max="0.10" step="0.01" format={v => Number(v).toFixed(2)} minLabel="0.01" maxLabel="0.10"
                    onChange={e => updatePpeSetting('smokeAreaRatioMin', parseFloat(e.target.value))}
                    description="Minimum smoke bounding box size as a fraction of the frame. Smaller detections are rejected as noise." />
                  <SliderRow label="Smoke Max Area Ratio" accent="blue"
                    value={ppeDetectionSettings.smokeAreaRatioMax ?? 0.80} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.smokeAreaRatioMax}
                    min="0.50" max="1.00" step="0.05" format={v => Number(v).toFixed(2)} minLabel="0.50" maxLabel="1.00"
                    onChange={e => updatePpeSetting('smokeAreaRatioMax', parseFloat(e.target.value))}
                    description="Maximum smoke bounding box size as a fraction of the frame. Larger detections are rejected (e.g. overcast sky or fog covering the whole view)." />
                </SettingsGroup>

                <SettingsGroup title="Smoke Detection Tuning" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(advancedThresholds, DEFAULT_ADVANCED_THRESHOLDS, ['smoke_temporal_verify', 'smoke_max_mean_brightness', 'smoke_min_texture_std', 'smoke_min_motion_ratio', 'smoke_min_bg_deviation', 'smoke_max_static_frames', 'smoke_required_consecutive'])}
                >
                  <p className="text-xs text-gray-500 -mt-1">Reduce false smoke alerts from bright concrete, haze, and dust on construction sites.</p>
                  <ToggleRow accent="blue" label="Require smoke to drift (temporal verify)"
                    checked={advancedThresholds.smoke_temporal_verify === 1}
                    onChange={e => updateAdvancedThreshold('smoke_temporal_verify', e.target.checked ? 1 : 0)}
                    description="Real smoke moves between frames; static haze/dust/shadows don't. Keep ON for outdoor cameras." />
                  <SliderRow label="Smoke Max Brightness" accent="blue"
                    value={advancedThresholds.smoke_max_mean_brightness} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.smoke_max_mean_brightness}
                    min="120" max="255" step="5"
                    onChange={e => updateAdvancedThreshold('smoke_max_mean_brightness', e.target.value)}
                    description="Reject near-white regions brighter than this as smoke (0–255). Bright concrete/sky sits ~200–230. Lower = stricter. Recommended: 190." />
                  <SliderRow label="Smoke Min Texture" accent="blue"
                    value={advancedThresholds.smoke_min_texture_std} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.smoke_min_texture_std}
                    min="4" max="30" step="1"
                    onChange={e => updateAdvancedThreshold('smoke_min_texture_std', e.target.value)}
                    description="Reject uniform surfaces (flat concrete/clear sky score 3–6) below this std-dev. Higher = stricter. Recommended: 12–16." />
                  <SliderRow label="Smoke Min Motion" accent="blue"
                    value={advancedThresholds.smoke_min_motion_ratio} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.smoke_min_motion_ratio}
                    min="0.01" max="0.20" step="0.01" format={v => `${(v * 100).toFixed(1)}%`}
                    onChange={e => updateAdvancedThreshold('smoke_min_motion_ratio', e.target.value)}
                    description="Minimum frame-to-frame movement for a region to count as drifting smoke. Higher = stricter (fewer false positives). Recommended: 5–8%." />
                  <SliderRow label="Background Deviation Threshold" accent="blue"
                    value={advancedThresholds.smoke_min_bg_deviation} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.smoke_min_bg_deviation}
                    min="5" max="40" step="1"
                    onChange={e => updateAdvancedThreshold('smoke_min_bg_deviation', e.target.value)}
                    description="Minimum mean pixel deviation from the background model required to classify a region as smoke. Higher = rejects more static haze/shadows (fewer false positives). Default: 15." />
                  <SliderRow label="Max Static Frames" accent="blue"
                    value={advancedThresholds.smoke_max_static_frames} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.smoke_max_static_frames}
                    min="1" max="30" step="1" format={v => `${v} frames`}
                    onChange={e => updateAdvancedThreshold('smoke_max_static_frames', e.target.value)}
                    description="Maximum consecutive frames a smoke detection can remain perfectly static before being rejected as a false positive. Default: 8." />
                  <SliderRow label="Smoke Required Consecutive Frames" accent="blue"
                    value={advancedThresholds.smoke_required_consecutive ?? 4} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.smoke_required_consecutive}
                    min="1" max="10" step="1" format={v => `${v} frames`} minLabel="1" maxLabel="10"
                    onChange={e => updateAdvancedThreshold('smoke_required_consecutive', e.target.value)}
                    description="Number of consecutive frames smoke must be detected before an alert fires. Default 4 avoids single-frame false positives. Lower to 1–2 for test/recorded video cameras where smoke appears briefly." />
                </SettingsGroup>

                <SettingsGroup title="Glasses Detection" accent="gray" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['glassesFaceRegionFraction', 'glassesFaceUpscale', 'glassesFaceCropConf', 'faceVisSkinMinRatio'])}
                >
                  <SliderRow label="Glasses Face Region Fraction" accent="gray"
                    value={ppeDetectionSettings.glassesFaceRegionFraction ?? 0.25} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.glassesFaceRegionFraction}
                    min="0.15" max="0.50" step="0.05" format={v => Number(v).toFixed(2)} minLabel="0.15" maxLabel="0.50"
                    onChange={e => updatePpeSetting('glassesFaceRegionFraction', parseFloat(e.target.value))}
                    description="Fraction of the detected face box used to search for safety glasses. Smaller values focus tighter on the eye region." />
                  <SliderRow label="Glasses Face Upscale Factor" accent="gray"
                    value={ppeDetectionSettings.glassesFaceUpscale ?? 3} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.glassesFaceUpscale}
                    min="1" max="5" step="1" minLabel="1" maxLabel="5"
                    onChange={e => updatePpeSetting('glassesFaceUpscale', parseInt(e.target.value))}
                    description="How much to enlarge the cropped face region before running glasses detection. Higher values help on small/distant faces but cost more processing per frame." />
                  <SliderRow label="Glasses Face Crop Confidence" accent="gray"
                    value={ppeDetectionSettings.glassesFaceCropConf ?? 0.20} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.glassesFaceCropConf}
                    min="0.10" max="0.80" step="0.05" format={v => Number(v).toFixed(2)} minLabel="0.10" maxLabel="0.80"
                    onChange={e => updatePpeSetting('glassesFaceCropConf', parseFloat(e.target.value))}
                    description="Minimum face-detection confidence required before attempting glasses detection on that face." />
                  <SliderRow label="Face Visibility Skin Min Ratio" accent="gray"
                    value={ppeDetectionSettings.faceVisSkinMinRatio ?? 0.10} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.faceVisSkinMinRatio}
                    min="0.02" max="0.30" step="0.01" format={v => `${Math.round(v * 100)}%`}
                    minLabel="2% (lenient)" maxLabel="30% (strict)"
                    onChange={e => updatePpeSetting('faceVisSkinMinRatio', parseFloat(e.target.value))}
                    description="Fraction of the face region that must be skin-toned for the face to count as visible — glasses violations are skipped when the face isn't visible. Default: 10%; lower it if faces at distance are wrongly treated as turned away, raise it to skip more back/side views." />
                </SettingsGroup>

                <SettingsGroup title="Fall Detection" accent="purple" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(advancedThresholds, DEFAULT_ADVANCED_THRESHOLDS, ['pose_confidence', 'fall_min_keypoint_confidence'])}
                >
                  <SliderRow label="Fall Detection Pose Confidence" accent="purple"
                    value={advancedThresholds.pose_confidence} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.pose_confidence}
                    min="0.1" max="0.6" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    onChange={e => updateAdvancedThreshold('pose_confidence', e.target.value)}
                    description="Minimum confidence for pose model to detect a person's body position. Default: 25%." />
                  <SliderRow label="Fall Keypoint Confidence" accent="purple"
                    value={advancedThresholds.fall_min_keypoint_confidence} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.fall_min_keypoint_confidence}
                    min="0.1" max="0.7" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    onChange={e => updateAdvancedThreshold('fall_min_keypoint_confidence', e.target.value)}
                    description="Minimum confidence for each body keypoint (shoulder, hip, ankle) to count in fall logic. Default: 30%." />
                </SettingsGroup>

                <SettingsGroup title="PPE Frame Filtering" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(advancedThresholds, DEFAULT_ADVANCED_THRESHOLDS, ['ppe_min_person_height'])}
                >
                  <SliderRow label="PPE Min Person Height" accent="blue"
                    value={advancedThresholds.ppe_min_person_height} defaultValue={DEFAULT_ADVANCED_THRESHOLDS.ppe_min_person_height}
                    min="0" max="300" step="10" format={v => (Number(v) === 0 ? 'Disabled' : `${v}px`)}
                    onChange={e => updateAdvancedThreshold('ppe_min_person_height', e.target.value)}
                    description="Skip PPE checks for people shorter than this many pixels in the frame. Filters out distant workers where PPE is undetectable. Set to 0 to disable (check all). Recommended: 100–150px." />
                </SettingsGroup>

                <div className="pt-1">
                  <p className="text-xs text-gray-500">
                    Changes are applied on next camera hot-reload (no restart needed). These settings override global defaults for this camera only.
                  </p>
                </div>
          </SettingsCard>

          {/* PPE Detection Behaviour */}
          <SettingsCard
            title="PPE Detection Behaviour"
            description="Tune hardhat and vest colour verification, grace windows, and worn thresholds"
            icon={AdjustmentsHorizontalIcon}
            open={showPpeDetectionSettings}
            onToggle={() => setShowPpeDetectionSettings(s => !s)}
            searchActive={advancedSearchActive}
            query={advancedSearch}
          >
                <div className="flex justify-end">
                  <button onClick={() => { setPpeDetectionSettings(DEFAULT_PPE_DETECTION_SETTINGS); setHasChanges(true); }} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                    Reset to Defaults
                  </button>
                </div>

                <SettingsGroup title="PPE Confidence Thresholds" accent="blue" defaultOpen searchActive={advancedSearchActive} query={advancedSearch}>
                  <p className="text-xs text-gray-500 px-1 pb-2">
                    <strong>Worn</strong> — minimum confidence to count PPE as present (lower = more sensitive).{' '}
                    <strong>Violation</strong> — minimum confidence to raise a missing-PPE alert (higher = fewer false alarms).
                  </p>
                  {PPE_ITEMS.map(item => {
                    const capability = getPpeItemCapability(item.key);
                    const isUnsupported = capability.status === 'needs_model';
                    return (
                      <div key={item.key} className={clsx('space-y-2 pb-3 border-b border-gray-100 last:border-0 last:pb-0 px-1', isUnsupported && 'opacity-60')}>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-xs font-medium text-gray-700">{item.label}</span>
                            <span className="ml-2 text-xs text-gray-400">{item.description}</span>
                          </div>
                          <CapabilityBadge capability={capability} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-green-600 font-medium">Worn</span>
                              <span className="text-xs font-mono bg-green-50 text-green-700 px-1.5 py-0.5 rounded">
                                {Math.round((ppeWornThresholds[item.key] ?? DEFAULT_PPE_WORN[item.key]) * 100)}%
                              </span>
                            </div>
                            <input type="range" min="0.10" max="0.90" step="0.05"
                              value={ppeWornThresholds[item.key] ?? DEFAULT_PPE_WORN[item.key]}
                              onChange={(e) => updatePpeWorn(item.key, e.target.value)}
                              disabled={isUnsupported}
                              className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600" />
                          </div>
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-red-600 font-medium">Violation</span>
                              <span className="text-xs font-mono bg-red-50 text-red-700 px-1.5 py-0.5 rounded">
                                {Math.round((ppeViolationThresholds[item.key] ?? DEFAULT_PPE_VIOLATION[item.key]) * 100)}%
                              </span>
                            </div>
                            <input type="range" min="0.10" max="0.99" step="0.05"
                              value={ppeViolationThresholds[item.key] ?? DEFAULT_PPE_VIOLATION[item.key]}
                              onChange={(e) => updatePpeViolation(item.key, e.target.value)}
                              disabled={isUnsupported}
                              className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600" />
                          </div>
                        </div>
                        {isUnsupported && <p className="text-xs text-slate-500">{capability.description}</p>}
                      </div>
                    );
                  })}
                </SettingsGroup>

                {/* Harness has its own card (not a row in PPE Confidence Thresholds) because it
                    runs on a SEPARATE model — harness-best-1.pt, classes harness / no-harness —
                    rather than the shared PPE model, and it ships behind a shadow-mode gate. */}
                <SettingsGroup title="Harness Detection" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['harnessConf', 'harnessCooldown', 'harnessShadowMode', 'harnessBurstWindow', 'harnessBurstMinSamples', 'harnessBurstMaxPresentRatio', 'harnessComplianceMemoryEnabled', 'harnessGraceFrames'])}
                >
                  <ToggleRow accent="blue" label="Shadow mode (log only, no alerts)"
                    checked={ppeDetectionSettings.harnessShadowMode ?? true}
                    onChange={e => updatePpeSetting('harnessShadowMode', e.target.checked)}
                    description="ON (default): the harness model runs and logs what it WOULD alert on, but raises no alerts — nothing reaches the dashboard, email or mobile. Turn OFF only after reviewing this camera's shadow logs, to take it live. Lets you validate one camera at a time while the rest of the fleet stays silent." />
                  <SliderRow label="Harness Confidence" accent="blue"
                    value={ppeDetectionSettings.harnessConf ?? 0.6} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.harnessConf}
                    min="0.20" max="0.90" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="20% (more detections)" maxLabel="90% (clear-cut only)"
                    onChange={e => updatePpeSetting('harnessConf', parseFloat(e.target.value))}
                    description="Minimum model confidence for a detection from the dedicated harness model to count. Applies to both directions equally (harness and no-harness). Lower = more detections and more violations raised; higher = only clear-cut cases. Default 60%." />
                  <SliderRow label="Harness Alert Cooldown" accent="blue"
                    value={ppeDetectionSettings.harnessCooldown ?? 45} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.harnessCooldown}
                    min="10" max="300" step="5" format={v => `${v}s`}
                    minLabel="10s" maxLabel="300s"
                    onChange={e => updatePpeSetting('harnessCooldown', parseInt(e.target.value))}
                    description="Minimum time before another harness alert can fire for the same person in the same zone, even if they stay unharnessed. Prevents one worker generating a stream of alerts. Default 45s." />

                  {/* False-positive gates. Until these existed, harness had NO false-positive
                      protection at all (unlike vest): a single bad frame could raise a HIGH-severity
                      alert. These mirror the vest burst-confirmation and compliance-memory gates. */}
                  <SliderRow label="Harness Burst Window" accent="blue"
                    value={ppeDetectionSettings.harnessBurstWindow ?? 8} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.harnessBurstWindow}
                    min="3" max="20" step="1" format={v => `${v} frames`}
                    minLabel="3 frames" maxLabel="20 frames"
                    onChange={e => updatePpeSetting('harnessBurstWindow', parseInt(e.target.value))}
                    description="Number of recent frames sampled to judge harness presence before a no-harness alert is confirmed. Harness alerts are HIGH severity and used to fire off a single bad frame — this buffer is what stops that. Larger = more evidence gathered, fewer false positives, slightly slower alerts. Default: 8 frames." />
                  <SliderRow label="Harness Burst Min Samples" accent="blue"
                    value={ppeDetectionSettings.harnessBurstMinSamples ?? 2} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.harnessBurstMinSamples}
                    min="1" max="15" step="1" format={v => `${v} frames`}
                    minLabel="1 frame (fastest)" maxLabel="15 frames (safest)"
                    onChange={e => updatePpeSetting('harnessBurstMinSamples', parseInt(e.target.value))}
                    description="Minimum number of frames of evidence inside the burst window before a no-harness can be confirmed. Raise it to reduce false positives on cameras where workers are briefly obscured by scaffolding or turn away; lower it for faster alerts. Default: 2 frames." />
                  <SliderRow label="Harness Burst Max Present Ratio" accent="blue"
                    value={ppeDetectionSettings.harnessBurstMaxPresentRatio ?? 0.25} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.harnessBurstMaxPresentRatio}
                    min="0.05" max="0.60" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="Stricter" maxLabel="Fewer false fires"
                    onChange={e => updatePpeSetting('harnessBurstMaxPresentRatio', parseFloat(e.target.value))}
                    description="If a harness was detected in more than this share of the burst window, hold the no-harness alert — the worker most likely is harnessed and the detector simply lost it for a few frames. Higher = more lenient, fewer false positives. Default: 25%." />
                  <ToggleRow accent="blue" label="Apply compliance memory to harness"
                    checked={ppeDetectionSettings.harnessComplianceMemoryEnabled ?? true}
                    onChange={e => updatePpeSetting('harnessComplianceMemoryEnabled', e.target.checked)}
                    description="ON (default): a worker seen recently wearing a harness gets the same compliance-memory grace already applied to vest and helmet, so a short run of bad frames will not raise an alert. Turn OFF only if you need every harness gap flagged immediately and accept the extra false positives." />
                  <SliderRow label="Harness Grace Frames" accent="blue"
                    value={ppeDetectionSettings.harnessGraceFrames ?? 45} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.harnessGraceFrames}
                    min="0" max="120" step="5" format={v => `${v} frames`}
                    minLabel="0 frames" maxLabel="120 frames"
                    onChange={e => updatePpeSetting('harnessGraceFrames', parseInt(e.target.value))}
                    description="How long to keep suppressing no-harness after the detector last confirmed a harness on this worker. Raise it for cameras where workers face away, work at odd angles, or are partly hidden by structure — a harness rarely comes off mid-task, so a longer window costs little and removes most false positives. Default: 45 (~9s @ 5fps)." />
                </SettingsGroup>

                <SettingsGroup title="Hardhat Colour Check" accent="blue" defaultOpen searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['hardhatColorCheckEnabled', 'hardhatColorMinRatio', 'hardhatGraceFrames', 'helmetWornMaxThreshold', 'hardhatSecondaryConf'])}
                >
                  <ToggleRow accent="blue" label="Enable hardhat colour suppression"
                    checked={ppeDetectionSettings.hardhatColorCheckEnabled}
                    onChange={e => updatePpeSetting('hardhatColorCheckEnabled', e.target.checked)}
                    description="Suppress a no-hardhat alert when the head region shows hard-hat colours, even if the object detector itself didn't confirm a hat." />
                  <SliderRow label="Min Hat Colour Ratio" accent="blue"
                    value={ppeDetectionSettings.hardhatColorMinRatio} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.hardhatColorMinRatio}
                    min="0.05" max="0.90" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="More Sensitive" maxLabel="Stricter"
                    onChange={e => updatePpeSetting('hardhatColorMinRatio', parseFloat(e.target.value))}
                    description="Fraction of the head region that must show hard-hat colours to confirm a hat by colour alone." />
                  <SliderRow label="Hat Grace Frames" accent="blue"
                    value={ppeDetectionSettings.hardhatGraceFrames} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.hardhatGraceFrames}
                    min="5" max="120" step="5" format={v => `${v} frames`}
                    minLabel="5 frames" maxLabel="120 frames"
                    onChange={e => updatePpeSetting('hardhatGraceFrames', parseInt(e.target.value))}
                    description="Frames to keep suppressing a no-hardhat alert after hat colour was last seen — bridges brief moments where the worker's head turns away or is partly hidden." />
                  <SliderRow label="Helmet Worn Max Threshold" accent="blue"
                    value={ppeDetectionSettings.helmetWornMaxThreshold} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.helmetWornMaxThreshold}
                    min="0.10" max="0.90" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="Lower (more detections)" maxLabel="Higher (fewer false worn)"
                    onChange={e => updatePpeSetting('helmetWornMaxThreshold', parseFloat(e.target.value))}
                    description="Upper cap on the confidence required to count a hard hat as worn, so a stricter global default can't make this camera too conservative." />
                  <SliderRow label="Hardhat Secondary Re-check Confidence" accent="blue"
                    value={ppeDetectionSettings.hardhatSecondaryConf ?? 0.25} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.hardhatSecondaryConf}
                    min="0.05" max="0.60" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="5% (suppress more)" maxLabel="60% (suppress less)"
                    onChange={e => updatePpeSetting('hardhatSecondaryConf', parseFloat(e.target.value))}
                    description="Confidence used for the low-threshold secondary model re-check that suppresses a no-hardhat alert when a helmet is found. Default: 25%; lower it to suppress more borderline cases, raise it to require a clearer helmet before suppressing." />
                </SettingsGroup>

                <SettingsGroup title="Hardhat Classifier (ML Gate)" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['hardhatClassifierEnabled'])}
                >
                  <ToggleRow accent="blue" label="Enable hardhat classifier suppression"
                    checked={!!ppeDetectionSettings.hardhatClassifierEnabled}
                    onChange={e => updatePpeSetting('hardhatClassifierEnabled', e.target.checked)}
                    description="Use AI classifier model to verify hardhat detections and suppress false Missing Hardhat alerts. Runs a secondary ML model on the cropped head region — when enabled, a confident HELMET prediction suppresses a head_nohelmet alert." />
                </SettingsGroup>

                <SettingsGroup title="Vest Colour Check" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['vestColorCheckEnabled', 'vestColorMinRatio', 'vestColorGraceFrames', 'vestTier1MeanSatMin', 'vestTier3LooseSilverMin'])}
                >
                  <ToggleRow accent="blue" label="Enable vest colour suppression"
                    checked={ppeDetectionSettings.vestColorCheckEnabled}
                    onChange={e => updatePpeSetting('vestColorCheckEnabled', e.target.checked)}
                    description="Suppress a no-vest alert when the torso region shows high-vis vest colours/stripes, even if the object detector missed it." />
                  <SliderRow label="Min Vest Colour Ratio" accent="blue"
                    value={ppeDetectionSettings.vestColorMinRatio} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.vestColorMinRatio}
                    min="0.01" max="0.30" step="0.01" format={v => `${(v * 100).toFixed(1)}%`}
                    minLabel="More Sensitive" maxLabel="Stricter"
                    onChange={e => updatePpeSetting('vestColorMinRatio', parseFloat(e.target.value))}
                    description="Minimum neon/stripe pixel ratio in the torso region required to confirm a vest by colour." />
                  <SliderRow label="Vest Grace Frames" accent="blue"
                    value={ppeDetectionSettings.vestColorGraceFrames} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.vestColorGraceFrames}
                    min="5" max="120" step="5" format={v => `${v} frames`}
                    minLabel="5 frames" maxLabel="120 frames"
                    onChange={e => updatePpeSetting('vestColorGraceFrames', parseInt(e.target.value))}
                    description="Frames to keep suppressing a no-vest alert after vest colour was last seen — bridges brief moments where the worker turns away." />
                  <SliderRow label="Tier-1 Mean Saturation Min" accent="blue"
                    value={ppeDetectionSettings.vestTier1MeanSatMin ?? 60} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.vestTier1MeanSatMin}
                    min="20" max="120" step="5"
                    minLabel="20 (lenient)" maxLabel="120 (strict)"
                    onChange={e => updatePpeSetting('vestTier1MeanSatMin', parseFloat(e.target.value))}
                    description="Minimum mean torso saturation for the colour+stripe vest tier — dark grey/brown clothing sits below this and is not treated as a vest. Default: 60; lower it if faded vests are being missed, raise it to reject dull clothing more aggressively." />
                  <SliderRow label="Loose Tier-3 Silver Min" accent="blue"
                    value={ppeDetectionSettings.vestTier3LooseSilverMin ?? 0.08} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.vestTier3LooseSilverMin}
                    min="0.02" max="0.30" step="0.01" format={v => `${Math.round(v * 100)}%`}
                    minLabel="2% (suppress more)" maxLabel="30% (suppress less)"
                    onChange={e => updatePpeSetting('vestTier3LooseSilverMin', parseFloat(e.target.value))}
                    description="Silver-stripe ratio needed for the loose stripe-only vest tier (dark vests with reflective tape, no neon colour). Default: 8%; raise it if grey/light clothing is being read as a vest, lower it if dark striped vests are missed." />
                </SettingsGroup>

                <SettingsGroup title="Vest / Helmet Detector (Primary)" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['vhEnabled', 'vhShadow', 'vhConf', 'vhContainment'])}
                >
                  <ToggleRow accent="blue" label="Enable Vest/Helmet detector (owns both verdicts)"
                    checked={ppeDetectionSettings.vhEnabled ?? true}
                    onChange={e => updatePpeSetting('vhEnabled', e.target.checked)}
                    description="Runs a dedicated vest/helmet model on the FULL frame and makes it the sole authority for both items — it can raise a violation as well as clear one, unlike the classifier gates below which can only suppress. Turning this OFF hands vest and helmet back to the general PPE model, which detects them far less reliably on site footage. When ON, the Vest Classifier, Hardhat Classifier and hard-hat colour/grace/secondary checks below no longer affect vest or helmet." />
                  <ToggleRow accent="blue" label="Shadow mode (log only, don't act)"
                    checked={ppeDetectionSettings.vhShadow ?? false}
                    onChange={e => updatePpeSetting('vhShadow', e.target.checked)}
                    description="Runs the detector and logs the verdict it WOULD apply ([PPE-VH-SHADOW]) without changing any violation — the general PPE model keeps deciding. Use this to validate the detector on a new camera before letting it act." />
                  <SliderRow label="Detection Confidence" accent="blue"
                    value={ppeDetectionSettings.vhConf ?? 0.5} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.vhConf}
                    min="0.20" max="0.90" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    onChange={e => updatePpeSetting('vhConf', parseFloat(e.target.value))}
                    description="Minimum model confidence for a vest/helmet detection to count. Applies to both directions equally (vest and no-vest, helmet and no-helmet). Lower = more detections and more violations raised; higher = only clear-cut cases. Default 50%." />
                  <SliderRow label="Person Containment" accent="blue"
                    value={ppeDetectionSettings.vhContainment ?? 0.5} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.vhContainment}
                    min="0.20" max="0.90" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    onChange={e => updatePpeSetting('vhContainment', parseFloat(e.target.value))}
                    description="How much of a vest/helmet box must sit inside a person's box to be attributed to them — this is what stops one worker's vest being credited to the person beside them. Lower it for cameras where workers are distant or partly clipped; raise it where people stand close together. Default 50%." />
                </SettingsGroup>

                <SettingsGroup title="Vest Classifier (ML Gate)" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['vestClassifierEnabled', 'vestClassifierShadow', 'vestClassifierConf', 'vestClassifierGraceFrames'])}
                >
                  <ToggleRow accent="blue" label="Enable vest classifier suppression"
                    checked={!!ppeDetectionSettings.vestClassifierEnabled}
                    onChange={e => updatePpeSetting('vestClassifierEnabled', e.target.checked)}
                    description="Runs a secondary ML model on the cropped person to confirm vest presence. When enabled, a 100% confident vest detection will suppress a MISSING_VEST alert even if colour/temporal checks disagree." />
                  <ToggleRow accent="blue" label="Shadow mode (log only, don't act)"
                    checked={ppeDetectionSettings.vestClassifierShadow ?? true}
                    onChange={e => updatePpeSetting('vestClassifierShadow', e.target.checked)}
                    description="Only applies when 'Enable vest classifier suppression' is OFF. In shadow mode the classifier still runs and logs what it WOULD do ([VEST-CLS-SHADOW] … WOULD suppress) without changing any alert — useful for validating the model before turning it on. Turn OFF to skip the classifier entirely when suppression is disabled." />
                  <SliderRow label="Classifier Confidence Threshold" accent="blue"
                    value={ppeDetectionSettings.vestClassifierConf ?? 0.75} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.vestClassifierConf}
                    min="0.30" max="0.95" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    onChange={e => updatePpeSetting('vestClassifierConf', parseFloat(e.target.value))}
                    description="Minimum classifier confidence required to suppress a MISSING_VEST alert. Lower = more lenient (borderline vest crops still suppress → fewer false positives); higher = stricter. Default 75%." />
                  <SliderRow label="Classifier Grace Frames" accent="blue"
                    value={ppeDetectionSettings.vestClassifierGraceFrames ?? 15} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.vestClassifierGraceFrames}
                    min="0" max="60" step="1" format={v => `${v} frames`}
                    onChange={e => updatePpeSetting('vestClassifierGraceFrames', parseInt(e.target.value))}
                    description="Frames to keep suppressing a MISSING_VEST alert after the classifier last confirmed a vest. Bridges momentary one-frame verdict flips (motion blur / edge crop / angle) that otherwise cause false positives. 0 = disabled. Default 15 (~3s)." />
                </SettingsGroup>

                <SettingsGroup title="Vest Abstain Gate" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['vestAbstainGateEnabled'])}
                >
                  <ToggleRow accent="blue" label="Vest Abstain Gate (defer no-vest when torso unassessable)"
                    checked={!!ppeDetectionSettings.vestAbstainGateEnabled}
                    onChange={e => updatePpeSetting('vestAbstainGateEnabled', e.target.checked)}
                    description="When enabled, defers a MISSING_VEST alert if the worker's torso is not clearly assessable (e.g. occluded, turned away, or out of frame), avoiding false fires until a confident view is available." />
                </SettingsGroup>

                <SettingsGroup title="Compliance Memory" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['complianceGraceRatio', 'complianceMemoryFrames'])}
                >
                  <SliderRow label="Compliance Grace Ratio" accent="blue"
                    value={ppeDetectionSettings.complianceGraceRatio ?? 0.7} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.complianceGraceRatio}
                    min="0.30" max="0.90" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="Fewer false fires" maxLabel="Stricter"
                    onChange={e => updatePpeSetting('complianceGraceRatio', parseFloat(e.target.value))}
                    description="Minimum share of recent frames a worker must be detected compliant to keep suppressing a violation. Lower it if compliant workers get false alerts when they briefly turn side-on. Default: 70%." />
                  <SliderRow label="Compliance Memory Frames" accent="blue"
                    value={ppeDetectionSettings.complianceMemoryFrames ?? 20} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.complianceMemoryFrames}
                    min="5" max="60" step="5" format={v => `${v} frames`}
                    minLabel="5 frames (fast)" maxLabel="60 frames (slow)"
                    onChange={e => updatePpeSetting('complianceMemoryFrames', parseInt(e.target.value))}
                    description="Rolling window size for compliance decay. Larger = compliance drops more slowly when the model misses a vest (e.g. worker turns away). Raise to 40 if workers frequently walk with their back to the camera. Default: 20 (~4s @ 5fps)." />
                </SettingsGroup>

                <SettingsGroup title="Vest Recovery" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['vestRecoveryGraceFrames'])}
                >
                  <SliderRow label="Vest Recovery Grace Frames" accent="blue"
                    value={ppeDetectionSettings.vestRecoveryGraceFrames ?? 45} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.vestRecoveryGraceFrames}
                    min="0" max="120" step="5" format={v => `${v} frames`}
                    minLabel="0 frames" maxLabel="120 frames"
                    onChange={e => updatePpeSetting('vestRecoveryGraceFrames', parseInt(e.target.value))}
                    description="How long to keep suppressing a no-vest after the multi-frame recovery last re-found a vest. Raise it for side-on / walk-through cameras where workers face away for several seconds. Default: 45 (~9s @ 5fps)." />
                </SettingsGroup>

                <SettingsGroup title="Vest Burst Confirmation" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['ppeBurstWindow', 'ppeBurstMinSamples', 'ppeBurstMaxPresentRatio'])}
                >
                  <SliderRow label="Burst Window" accent="blue"
                    value={ppeDetectionSettings.ppeBurstWindow ?? 8} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.ppeBurstWindow}
                    min="3" max="20" step="1" format={v => `${v} frames`}
                    minLabel="3 frames" maxLabel="20 frames"
                    onChange={e => updatePpeSetting('ppeBurstWindow', parseInt(e.target.value))}
                    description="Number of recent frames sampled to judge vest presence before a no-vest alert is confirmed." />
                  <SliderRow label="Burst Min Samples" accent="blue"
                    value={ppeDetectionSettings.ppeBurstMinSamples ?? 5} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.ppeBurstMinSamples}
                    min="1" max="15" step="1" format={v => `${v} frames`}
                    minLabel="1 frame" maxLabel="15 frames"
                    onChange={e => updatePpeSetting('ppeBurstMinSamples', parseInt(e.target.value))}
                    description="Minimum number of frames within the burst window that must show no vest before the no-vest alert is confirmed." />
                  <SliderRow label="Burst Max Vest-Present Ratio" accent="blue"
                    value={ppeDetectionSettings.ppeBurstMaxPresentRatio ?? 0.25} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.ppeBurstMaxPresentRatio}
                    min="0.05" max="0.60" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="Stricter" maxLabel="Fewer false fires"
                    onChange={e => updatePpeSetting('ppeBurstMaxPresentRatio', parseFloat(e.target.value))}
                    description="If a vest was detected in more than this share of the burst window, hold the no-vest alert. Higher = more lenient. Default: 25%." />
                </SettingsGroup>

                <SettingsGroup title="Boots Colour Check" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['bootsColorMinRatio'])}
                >
                  <SliderRow label="Min Boots Colour Ratio" accent="blue"
                    value={ppeDetectionSettings.bootsColorMinRatio ?? 0.15} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.bootsColorMinRatio}
                    min="0.05" max="0.50" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="More Sensitive" maxLabel="Stricter"
                    onChange={e => updatePpeSetting('bootsColorMinRatio', parseFloat(e.target.value))}
                    description="Min share of the feet region that must be dark footwear to suppress a no-boots violation. Default: 15%." />
                </SettingsGroup>

                <SettingsGroup title="Occlusion Suppression" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['occlusionCautionThreshold', 'occlusionSuppressThreshold'])}
                >
                  <SliderRow label="Caution Threshold" accent="blue"
                    value={ppeDetectionSettings.occlusionCautionThreshold ?? 0.2} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.occlusionCautionThreshold}
                    min="0.05" max="0.60" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="Earlier caution" maxLabel="Later"
                    onChange={e => updatePpeSetting('occlusionCautionThreshold', parseFloat(e.target.value))}
                    description="Occlusion ratio above which the system starts masking out the occluding object's colour, to avoid misreading it as PPE." />
                  <SliderRow label="Suppress Threshold" accent="blue"
                    value={ppeDetectionSettings.occlusionSuppressThreshold ?? 0.4} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.occlusionSuppressThreshold}
                    min="0.10" max="0.80" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="More Sensitive" maxLabel="Suppress more"
                    onChange={e => updatePpeSetting('occlusionSuppressThreshold', parseFloat(e.target.value))}
                    description="Occlusion ratio above which a PPE violation is suppressed (the worker is too hidden to judge). Lower it on crowded cameras to cut false alerts. Default: 40%." />
                </SettingsGroup>

                <SettingsGroup title="Experimental (shadow-tested)" accent="amber" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['vestTier3Strict', 'vestTier3SilverMin', 'hardhatWhiteLenient', 'hardhatWhiteMinRatio'])}
                >
                  <p className="text-xs text-gray-500 -mt-1">
                    Leave OFF to measure impact in the engine logs first. Turn ON per-camera once the trade-off is confirmed.
                  </p>
                  <ToggleRow accent="amber" label="Strict vest stripe tier (reject grey clothing read as vest)"
                    checked={!!ppeDetectionSettings.vestTier3Strict}
                    onChange={e => updatePpeSetting('vestTier3Strict', e.target.checked)}
                    description="Requires a structured cross-stripe pattern (not diffuse silver) for the stripe-only vest tier — stops grey clothing from being read as a vest." />
                  <SliderRow label="Strict Tier-3 Silver Min" accent="amber"
                    value={ppeDetectionSettings.vestTier3SilverMin ?? 0.12} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.vestTier3SilverMin}
                    min="0.08" max="0.30" step="0.01" format={v => `${Math.round(v * 100)}%`}
                    minLabel="More Sensitive" maxLabel="Stricter"
                    onChange={e => updatePpeSetting('vestTier3SilverMin', parseFloat(e.target.value))}
                    description={'Only applies when "Strict vest stripe tier" is on. Required silver-stripe density for a dark/teal vest to be confirmed without hi-vis colour. Default: 12%.'} />
                  <ToggleRow accent="amber" label="Lenient white hard-hat rescue (specular glint)"
                    checked={!!ppeDetectionSettings.hardhatWhiteLenient}
                    onChange={e => updatePpeSetting('hardhatWhiteLenient', e.target.checked)}
                    description="Enables a lenient specular-highlight path that rescues WHITE hard hats the normal geometry check misses." />
                  <SliderRow label="White-Hat Min Ratio" accent="amber"
                    value={ppeDetectionSettings.hardhatWhiteMinRatio ?? 0.20} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.hardhatWhiteMinRatio}
                    min="0.10" max="0.50" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="More Sensitive" maxLabel="Stricter"
                    onChange={e => updatePpeSetting('hardhatWhiteMinRatio', parseFloat(e.target.value))}
                    description={'Only applies when "Lenient white hard-hat rescue" is on. Min white/grey share of the head region (with a specular glint) to confirm a white hat. Default: 20%.'} />
                </SettingsGroup>

                <SettingsGroup title="General PPE Settings" accent="blue" searchActive={advancedSearchActive} query={advancedSearch}
                  badge={countModified(ppeDetectionSettings, DEFAULT_PPE_DETECTION_SETTINGS, ['historyFrames', 'minAverageConfidence'])}
                >
                  <SliderRow label="PPE History Frames" accent="blue"
                    value={ppeDetectionSettings.historyFrames} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.historyFrames}
                    min="1" max="10" step="1" format={v => `${v} frames`}
                    minLabel="1 frame" maxLabel="10 frames"
                    onChange={e => updatePpeSetting('historyFrames', parseInt(e.target.value))}
                    description="Voting window size for PPE presence. Higher values reduce flicker but increase detection latency. Default: 3." />
                  <SliderRow label="Min Average PPE Confidence" accent="blue"
                    value={ppeDetectionSettings.minAverageConfidence ?? 0.75} defaultValue={DEFAULT_PPE_DETECTION_SETTINGS.minAverageConfidence}
                    min="0.30" max="0.95" step="0.05" format={v => `${Math.round(v * 100)}%`}
                    minLabel="30% (sensitive)" maxLabel="95% (strict)"
                    onChange={e => updatePpeSetting('minAverageConfidence', parseFloat(e.target.value))}
                    description="Minimum average confidence across all PPE items before an alert fires. Lower = more sensitive. Default: 75%." />
                  <div>
                    <label className="text-sm text-gray-700 block mb-1">Violation Frame Overrides</label>
                    <textarea
                      rows={3}
                      className="w-full text-xs font-mono border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder={'{"no_helmet": 5, "no_vest": 4, "no_harness": 6}'}
                      value={typeof ppeDetectionSettings.violationFrameOverrides === 'object'
                        ? JSON.stringify(ppeDetectionSettings.violationFrameOverrides, null, 2)
                        : ppeDetectionSettings.violationFrameOverrides}
                      onChange={e => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          updatePpeSetting('violationFrameOverrides', parsed);
                        } catch {
                          updatePpeSetting('violationFrameOverrides', e.target.value);
                        }
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Per-class frame count overrides as JSON object. Overrides historyFrames for specific violation types — including <code>no_harness</code>, e.g. <code>{'{"no_harness": 6}'}</code> to demand more frames of evidence before a HIGH-severity harness alert. Empty object = use historyFrames for all.
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-700 block mb-1">Enabled Violation Classes</label>
                    <textarea
                      rows={2}
                      className="w-full text-xs font-mono border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder={'["no_helmet", "no_vest"]  ← empty = report all'}
                      value={Array.isArray(ppeDetectionSettings.enabledViolationClasses)
                        ? JSON.stringify(ppeDetectionSettings.enabledViolationClasses)
                        : ppeDetectionSettings.enabledViolationClasses}
                      onChange={e => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          updatePpeSetting('enabledViolationClasses', parsed);
                        } catch {
                          updatePpeSetting('enabledViolationClasses', e.target.value);
                        }
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      JSON array of violation class names to report for this camera. Empty array = report all violation types.
                    </p>
                  </div>
                </SettingsGroup>

                <SettingsGroup title="Vehicle Proximity" accent="gray" searchActive={advancedSearchActive} query={advancedSearch}>
                  <SliderRow label="Critical Distance (Immediate Danger)" accent="gray"
                    value={vehicleProximitySettings.criticalDistance} defaultValue={2.0}
                    min="0.5" max="5" step="0.5" format={v => `< ${Number(v).toFixed(1)}m`}
                    onChange={e => updateVehicleProximity('criticalDistance', parseFloat(e.target.value))}
                    description="Alert as CRITICAL when a person is within this distance of a vehicle." />
                  <SliderRow label="High Distance (Dangerous Proximity)" accent="gray"
                    value={vehicleProximitySettings.highDistance} defaultValue={5.0}
                    min="2" max="10" step="0.5" format={v => `< ${Number(v).toFixed(1)}m`}
                    onChange={e => updateVehicleProximity('highDistance', parseFloat(e.target.value))}
                    description="Alert as HIGH when a person is within this distance of a vehicle." />
                  <SliderRow label="Medium Distance (Warning Zone)" accent="gray"
                    value={vehicleProximitySettings.mediumDistance} defaultValue={10.0}
                    min="5" max="20" step="1" format={v => `< ${Number(v).toFixed(0)}m`}
                    onChange={e => updateVehicleProximity('mediumDistance', parseFloat(e.target.value))}
                    description="Alert as MEDIUM when a person is within this distance of a vehicle." />
                  <SliderRow label="Minimum Vehicle Size" accent="gray"
                    value={vehicleProximitySettings.minSizeRatio} defaultValue={0.02}
                    min="0.005" max="0.10" step="0.005" format={v => `${(v * 100).toFixed(1)}% of frame`}
                    onChange={e => updateVehicleProximity('minSizeRatio', parseFloat(e.target.value))}
                    description="Ignores vehicle detections smaller than this fraction of the frame — filters out distant/background vehicles." />
                  <SliderRow label="Assumed Person Height" accent="gray"
                    value={ppeDetectionSettings.assumedPersonHeightMeters ?? 1.7} defaultValue={1.7}
                    min="1.4" max="2.0" step="0.05" format={v => `${Number(v).toFixed(2)}m`}
                    onChange={e => updatePpeSetting('assumedPersonHeightMeters', parseFloat(e.target.value))}
                    description="Real-world person height used to convert pixel distances to metres. Lower = larger estimated distances (more alerts)." />
                  <ToggleRow accent="gray" label="Also alert on idle / stationary vehicles"
                    checked={!!vehicleProximitySettings.detectIdleVehicles}
                    onChange={e => updateVehicleProximity('detectIdleVehicles', e.target.checked)}
                    description="By default, alerts only fire while the vehicle is in transition (moving). Enable this to also alert when a nearby vehicle is stopped." />
                  <SliderRow label="Motion Window (frames)" accent="gray"
                    value={vehicleProximitySettings.motionWindowFrames ?? 15} defaultValue={15}
                    min="5" max="30" step="1" format={v => `${Number(v).toFixed(0)} frames`}
                    onChange={e => updateVehicleProximity('motionWindowFrames', parseInt(e.target.value, 10))}
                    description="Advanced: frames the moving/idle classifier averages over. Longer smooths out detection jitter on large/occluded vehicles." />
                  <SliderRow label="Idle Displacement Ratio" accent="gray"
                    value={vehicleProximitySettings.idleDisplacementRatio ?? 0.06} defaultValue={0.06}
                    min="0.02" max="0.20" step="0.01" format={v => `${(v * 100).toFixed(0)}% of box`}
                    onChange={e => updateVehicleProximity('idleDisplacementRatio', parseFloat(e.target.value))}
                    description="Advanced: net bbox-center movement (as a fraction of the vehicle's box diagonal) needed to count as moving. Higher = more jitter tolerance." />
                  <SliderRow label="Motion Direction Ratio" accent="gray"
                    value={vehicleProximitySettings.motionDirectionRatio ?? 0.5} defaultValue={0.5}
                    min="0.1" max="0.9" step="0.05" format={v => Number(v).toFixed(2)}
                    onChange={e => updateVehicleProximity('motionDirectionRatio', parseFloat(e.target.value))}
                    description="Advanced: net÷total path length. Real motion travels straight (→1); jitter zigzags (→0). Higher = stricter, rejects more jitter." />
                  <SliderRow label="Motion Confirm Frames" accent="gray"
                    value={vehicleProximitySettings.motionConfirmFrames ?? 3} defaultValue={3}
                    min="1" max="10" step="1" format={v => `${Number(v).toFixed(0)} frames`}
                    onChange={e => updateVehicleProximity('motionConfirmFrames', parseInt(e.target.value, 10))}
                    description="Advanced: consecutive moving frames required before a moving-only alert fires. Higher = fewer false alerts on parked vehicles, slightly slower to catch a real drive-by." />
                </SettingsGroup>

                <div className="pt-1">
                  <p className="text-xs text-gray-500">
                    Changes are applied on next camera hot-reload (no restart needed). Field names match ai-engine config exactly.
                  </p>
                </div>
          </SettingsCard>

          {/* Alert Cooldowns */}
          <SettingsCard
            title="Alert Cooldowns"
            description="Minimum time between repeated alerts per detection type (seconds)"
            icon={AdjustmentsHorizontalIcon}
            open={showAlertCooldowns}
            onToggle={() => setShowAlertCooldowns(s => !s)}
            searchActive={advancedSearchActive}
            query={advancedSearch}
          >
                <div className="flex justify-end">
                  <button onClick={() => { setAlertCooldowns(DEFAULT_ALERT_COOLDOWNS); setHasChanges(true); }} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                    Reset to Defaults
                  </button>
                </div>
                {[
                  { key: 'ppe', label: 'PPE Violation', min: 5, max: 300, default: 30 },
                  { key: 'fire', label: 'Fire / Smoke', min: 5, max: 600, default: 120 },
                  { key: 'fall', label: 'Fall Detection', min: 5, max: 300, default: 60 },
                  { key: 'vehicle_proximity', label: 'Vehicle Proximity', min: 5, max: 300, default: 30 },
                  { key: 'crowd', label: 'Crowd Density', min: 5, max: 300, default: 60 },
                  { key: 'trespass', label: 'Trespass', min: 5, max: 300, default: 30 },
                ].filter(({ label }) => matchesAdvancedSearch(label)).map(({ key, label, min, max, default: def }) => (
                  <SliderRow key={key} label={label} accent="blue"
                    value={alertCooldowns[key] ?? DEFAULT_ALERT_COOLDOWNS[key]} defaultValue={def}
                    min={min} max={max} step="5" format={v => `${v}s`}
                    minLabel={`${min}s`} maxLabel={`${max}s`}
                    onChange={e => { setAlertCooldowns(p => ({ ...p, [key]: parseInt(e.target.value) })); setHasChanges(true); }}
                    description={`Minimum time before another ${label} alert can fire on this camera, even if the condition persists. Prevents alert spam from a single ongoing event.`} />
                ))}
          </SettingsCard>

          {/* Crowd Density Settings */}
          <SettingsCard
            title="Crowd Density"
            description="Per-camera person count threshold and alert cooldown"
            icon={AdjustmentsHorizontalIcon}
            open={showCrowdDensitySettings}
            onToggle={() => setShowCrowdDensitySettings(s => !s)}
            searchActive={advancedSearchActive}
            query={advancedSearch}
          >
                <div className="flex justify-end">
                  <button onClick={() => { setCrowdDensitySettings(DEFAULT_CROWD_DENSITY); setHasChanges(true); }} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                    Reset to Defaults
                  </button>
                </div>
                {matchesAdvancedSearch('Max Persons Threshold') && (
                  <SliderRow label="Max Persons Threshold" accent="blue"
                    value={crowdDensitySettings.maxPersons} defaultValue={DEFAULT_CROWD_DENSITY.maxPersons}
                    min="2" max="50" step="1" format={v => `${v} persons`}
                    minLabel="2" maxLabel="50"
                    onChange={e => { setCrowdDensitySettings(p => ({ ...p, maxPersons: parseInt(e.target.value) })); setHasChanges(true); }}
                    description="Alert fires when this many persons are detected simultaneously. 0 = use global setting." />
                )}
                {matchesAdvancedSearch('Alert Cooldown') && (
                  <SliderRow label="Alert Cooldown" accent="blue"
                    value={crowdDensitySettings.cooldown} defaultValue={DEFAULT_CROWD_DENSITY.cooldown}
                    min="10" max="300" step="5" format={v => `${v}s`}
                    minLabel="10s" maxLabel="300s"
                    onChange={e => { setCrowdDensitySettings(p => ({ ...p, cooldown: parseInt(e.target.value) })); setHasChanges(true); }}
                    description="Minimum time between repeated crowd density alerts on this camera once the person threshold is exceeded." />
                )}
          </SettingsCard>
          </>
          )}

          {/* Analytics Section */}
          {activeTab === 'analytics' && (
          <div className="card">
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <Cog6ToothIcon className="w-5 h-5" />
                Detection Analytics
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {enabledCount} of {ANALYTICS_OPTIONS.length} enabled
              </p>
            </div>
            <div>
              {analyticGroups.map(group => (
                <div key={group.title} className="border-t first:border-t-0">
                  <div className="bg-slate-50 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{group.title}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{group.description}</p>
                  </div>
                  <div className="divide-y">
                    {group.options.map(option => {
                      const analyticState = analytics.find(a => a.id === option.id);
                      const isEnabled = analyticState?.enabled || false;
                      const Icon = option.icon;
                      const capability = getAnalyticCapability(option.id);
                      const blocked = !isEnabled && (
                        !canEnableCapability(capability) ||
                        (capability.status === 'needs_zone' && option.requiresZones && availableZones.length === 0)
                      );

                      return (
                        <div key={option.id} className={clsx('p-4', blocked && 'opacity-70')}>
                          <div
                            className={clsx('flex items-start gap-3', blocked ? 'cursor-not-allowed' : 'cursor-pointer')}
                            onClick={() => toggleAnalytic(option.id)}
                          >
                            <div className={clsx(
                              'p-2 rounded-lg mt-0.5',
                              isEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
                            )}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-medium text-gray-900">{option.name}</p>
                                <span className={clsx(
                                  'text-xs px-1.5 py-0.5 rounded',
                                  option.severity === 'CRITICAL' && 'bg-red-100 text-red-700',
                                  option.severity === 'HIGH' && 'bg-orange-100 text-orange-700',
                                  option.severity === 'MEDIUM' && 'bg-yellow-100 text-yellow-700',
                                  option.severity === 'LOW' && 'bg-blue-100 text-blue-700',
                                )}>
                                  {option.severity}
                                </span>
                                <CapabilityBadge capability={capability} />
                              </div>
                              <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                              <p className="text-xs text-slate-400 mt-1">{capability.description}</p>
                            </div>
                            <div className={clsx(
                              'w-10 h-6 rounded-full transition-colors flex-shrink-0',
                              isEnabled ? 'bg-blue-600' : 'bg-gray-200'
                            )}>
                              <div className={clsx(
                                'w-5 h-5 bg-white rounded-full shadow transform transition-transform mt-0.5',
                                isEnabled ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'
                              )} />
                            </div>
                          </div>

                          {/* Zone selector - only shown when enabled */}
                          {isEnabled && (
                            <div className="mt-3 ml-11 flex items-center gap-2">
                              <span className="text-sm text-gray-500">Apply to:</span>
                              <ZoneSelector
                                zones={zones}
                                selectedZones={analyticState?.zones || ['all']}
                                onChange={(newZones) => updateAnalyticZones(option.id, newZones)}
                                disabled={option.requiresZones && zones.length === 0}
                              />
                              {option.requiresZones && zones.length === 0 && (
                                <span className="text-xs text-orange-500">Requires zones</span>
                              )}
                            </div>
                          )}

                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Camera Info */}
          {activeTab === 'info' && (
          <div className="card p-4">
            <h3 className="font-semibold mb-3">Camera Information</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className={clsx(
                  'font-medium',
                  camera.status === 'online' ? 'text-green-600' : 'text-gray-400'
                )}>
                  {camera.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">AI Device</span>
                <span className="font-medium">
                  {camera.aiDevice?.name || 'Not assigned'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Device Status</span>
                <span className={clsx(
                  'font-medium',
                  camera.aiDevice?.status === 'online' ? 'text-green-600' : 'text-gray-400'
                )}>
                  {camera.aiDevice?.status || 'N/A'}
                </span>
              </div>
            </div>
          </div>
          )}
          </div>
        </div>
      </div>

      {/* Zone Assignment Modal */}
      {showZoneAssignmentModal && (
        <ZoneAssignmentModal
          isOpen={showZoneAssignmentModal}
          onClose={() => {
            setShowZoneAssignmentModal(false);
            setIsDrawing(false);
            setCurrentPoints([]);
            setNewZoneName('');
            setSelectedZoneType('restricted');
          }}
          cameraId={camera?.id}
          siteId={camera?.siteId}
          existingZoneIds={zones.map(z => z.dbId || z.id).filter(Boolean)}
          onZoneAssigned={(zone) => {
            handleZoneAssigned(zone);
            setShowZoneAssignmentModal(false);
          }}
          currentPoints={currentPoints}
          setCurrentPoints={setCurrentPoints}
          onDrawingModeChange={handleDrawingModeChange}
        />
      )}
    </div>
  );
}
